// InvoiceFinanceSourcing.jsx
// Invoice finance sourcing — layout and styling aligned with BorrowerSourcing (public BTL sourcing).
// Changes from original:
//   • Facility required auto-fills from turnover but stays editable (like projected turnover)
//   • Invoice value auto-calculates in the background (not shown in UI)
//   • Additional borrowing calculations use real DB fields via calcAdditionalBorrowing()
//   • abValue / abType passed through to mapSourcingApiProducts so each card gets its own abCalc
//   • InvoiceResultCard shows additional borrowing in Full details only

import React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Bookmark, BookmarkCheck, Check, ChevronDown, Info,
  Loader2, MessageSquareHeart, ShieldCheck, ShoppingCart, SlidersHorizontal, Trash2, X,
} from "lucide-react";
import TopNav from "../../components/TopNav.jsx";
import CartMascotNudge from "../../components/CartMascotNudge.jsx";
import { rememberSourcingCategory, shortlistPathForCategory } from "../../utils/sourcingCategory.js";
import ReviewPromptModal from "../../components/reviews/ReviewPromptModal.jsx";
import SourcingSignupPrompt from "../../components/auth/SourcingSignupPrompt.jsx";
import MobileQuickValidationDialog, { isMobileViewport } from "../../components/sourcing/MobileQuickValidationDialog.jsx";
import SourcingLiveCount from "../../components/sourcing/SourcingLiveCount.jsx";
import { useSourcingEngagement, useReviewPrompt, REVIEW_PROMPT_DELAY_MS, SOURCING_FEEDBACK_BUTTON_CLASS, SOURCING_FEEDBACK_MOBILE_BAR_BUTTON_CLASS } from "../../hooks/useSourcingEngagement.js";
import { isAuthenticated, getBorrowerEmail } from "../../utils/auth.js";
import { STORAGE_KEYS } from "../../utils/constants.js";
import {
  addShortlistedProduct,
  removeShortlistedProductByMortgageProduct,
  getShortlistedProducts,
} from "../../api/borrowerPortal/shortlistedModal.js";
import { trackInvoiceFinanceClick } from "../../api/clickTracking.js";
import {
  buildInvoiceFinanceSourcingPayload,
  getInvoiceFinanceSourcingFilters,
  peekInvoiceFinanceCatalogueCache,
} from "../../api/sourcing-api/invoiceFinanceSourcingApi.js";
import {
  INVOICE_FINANCE_SECTORS,
  INVOICE_FINANCE_SECTOR_BLURBS,
  buildInvoiceFinanceShortlistProductData,
  calcFacilityFromTurnover,
  filterInvoiceFinanceSectorOptions,
  filterInvoiceFinanceSourcingProducts,
  formatInvoiceFinanceSectorLabel,
  isExcludedInvoiceFinanceSector,
  isInvoiceFinanceConstructionSector,
  isInvoiceFinanceProductScheduled,
  mapSourcingApiProducts,
  matchesInvoiceFinanceSector,
  normalizePercentScale,
  productMatchesFacilityBand,
  readProductLimitBand,
  productMatchesInvoiceEligibility,
  resolveInvoiceFinanceContractTermMonths,
  formatInvoiceFinanceProductTitle,
} from "../../api/invoiceFinanceSourcing.js";

function getScrollParent(node) {
  let el = node?.parentElement;
  while (el) {
    const oy = getComputedStyle(el).overflowY;
    if (/(auto|scroll)/.test(oy) && el.scrollHeight > el.clientHeight) return el;
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

function readScrollTop(container, isWindow) {
  if (isWindow) return window.scrollY || document.documentElement.scrollTop || 0;
  return container.scrollTop;
}

function writeScrollTop(container, isWindow, top) {
  if (isWindow) {
    const root = document.scrollingElement || document.documentElement;
    root.scrollTop = top;
    window.scrollTo(0, top);
  } else {
    container.scrollTop = top;
  }
}

const IF_CART_MASCOT_SEEN_KEY = "if_cart_mascot_seen";
const IF_SIGNUP_PROMPT_KEY = "if_sourcing_signup_prompt_seen";

/** Number of product cards a guest can see before the sign-up gate. */
const GUEST_FREE_PRODUCTS = 1;

/**
 * Slow "bumpy" scroll: discrete steps with short pauses so the user can glance
 * at products on the way. Lands so `target` and any companion nodes (e.g. the
 * existing-facility panel) are fully visible together. Returns a cancel fn, or
 * null if already in place.
 */
function bumpyScrollToProduct(target, {
  stepPx = 420,
  pauseMs = 800,
  settleMs = 80,
  margin = 24,
  topPad = 96,
  companions = [],
} = {}) {
  if (!target) return null;

  const container = getScrollParent(target);
  const isWindow =
    container === document.scrollingElement ||
    container === document.documentElement ||
    container === document.body;

  const measureUnionRect = () => {
    const nodes = [target, ...companions].filter(Boolean);
    let top = Infinity;
    let bottom = -Infinity;
    for (const el of nodes) {
      const r = el.getBoundingClientRect();
      top = Math.min(top, r.top);
      bottom = Math.max(bottom, r.bottom);
    }
    if (!Number.isFinite(top) || !Number.isFinite(bottom)) return null;
    return { top, bottom, height: bottom - top };
  };

  const measureEndTop = () => {
    const union = measureUnionRect();
    if (!union) return readScrollTop(container, isWindow);

    const startTop = readScrollTop(container, isWindow);
    const cRect = isWindow ? null : container.getBoundingClientRect();
    const viewTop = isWindow ? 0 : cRect.top;
    const viewBottom = isWindow ? window.innerHeight : cRect.top + container.clientHeight;
    const viewHeight = viewBottom - viewTop;
    const usable = Math.max(80, viewHeight - topPad - margin);

    // Delta D: scrolling down by D moves content up by D in the viewport.
    const dMin = union.bottom - (viewBottom - margin); // enough to reveal bottom
    const dMax = union.top - (viewTop + topPad); // not so much that top clips

    let delta;
    if (union.height <= usable && dMin <= dMax) {
      // Both fit: prefer a centred landing within the valid range.
      const dCenter = (union.top + union.bottom) / 2 - (viewTop + viewHeight / 2);
      delta = Math.min(dMax, Math.max(dMin, dCenter));
    } else if (union.height <= usable) {
      // Range inverted due to layout noise — bias toward showing the bottom edge.
      delta = dMin;
    } else {
      // Taller than the viewport: keep the top of the pair under the sticky bar.
      delta = dMax;
    }

    return Math.max(0, startTop + delta);
  };

  let endTop = measureEndTop();
  let current = readScrollTop(container, isWindow);
  if (Math.abs(endTop - current) < 2) return null;

  // Start from above the results so earlier products pass through the viewport.
  const panel = document.getElementById("invoice-results");
  if (panel) {
    const panelRect = panel.getBoundingClientRect();
    const startTop = readScrollTop(container, isWindow);
    const viewportTop = isWindow ? 0 : container.getBoundingClientRect().top;
    const panelScrollTop = startTop + (panelRect.top - viewportTop) - 16;
    writeScrollTop(container, isWindow, Math.max(0, Math.min(panelScrollTop, endTop)));
    current = readScrollTop(container, isWindow);
    endTop = measureEndTop();
  }

  let cancelled = false;
  let timerId = 0;

  const tick = () => {
    if (cancelled) return;
    endTop = measureEndTop();
    current = readScrollTop(container, isWindow);
    const remaining = endTop - current;
    if (Math.abs(remaining) < 8) {
      writeScrollTop(container, isWindow, endTop);
      return;
    }

    const direction = Math.sign(remaining) || 1;
    const step = direction * Math.min(stepPx, Math.abs(remaining));
    writeScrollTop(container, isWindow, current + step);

    timerId = window.setTimeout(tick, pauseMs + settleMs);
  };

  timerId = window.setTimeout(tick, pauseMs);

  return () => {
    cancelled = true;
    window.clearTimeout(timerId);
  };
}

// ─── Formatters ───────────────────────────────────────────────────────────────
const Num = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })
    : "—";

/** Review-limit chip: £1M+ shown as 1M / 1.5M, otherwise full amount. */
const formatReviewLimitChip = (n) => {
  const amount = Number(n);
  if (!Number.isFinite(amount)) return "—";
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    const label = Number.isInteger(millions)
      ? String(millions)
      : String(Math.round(millions * 10) / 10);
    return `${label}M`;
  }
  return Num(amount);
};

/** Compact chip money: 1M+ → 1M, 1k+ → 405k, else full amount. */
const formatCompactMoneyChip = (n) => {
  const amount = Number(n);
  if (!Number.isFinite(amount)) return "—";
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    const label = Number.isInteger(millions)
      ? String(millions)
      : String(Math.round(millions * 10) / 10);
    return `${label}M`;
  }
  if (amount >= 1_000) {
    const thousands = amount / 1_000;
    const label = Number.isInteger(thousands)
      ? String(thousands)
      : String(Math.round(thousands * 10) / 10);
    return `${label}k`;
  }
  return Num(amount);
};

const formatPercentRaw = (n) => {
  if (!Number.isFinite(Number(n))) return "—";
  const rounded = Math.round(Number(n) * 1e8) / 1e8;
  if (Number.isInteger(rounded)) return `${rounded}`;
  return rounded.toFixed(8).replace(/\.?0+$/, "");
};

/** Ease number displays when facility/filters recalculate — avoids abrupt jumps. */
function useSmoothNumber(target, { duration = 420, decimals = 0 } = {}) {
  const targetNum = Number(target);
  const hasTarget = Number.isFinite(targetNum);
  const [display, setDisplay] = React.useState(hasTarget ? targetNum : 0);
  const displayRef = React.useRef(hasTarget ? targetNum : 0);
  const hasMountedRef = React.useRef(false);
  const rafRef = React.useRef(0);

  React.useEffect(() => {
    if (!hasTarget) {
      displayRef.current = 0;
      setDisplay(0);
      return undefined;
    }
    // First paint: snap to value (no count-up from zero).
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      displayRef.current = targetNum;
      setDisplay(targetNum);
      return undefined;
    }
    const from = displayRef.current;
    const to = targetNum;
    if (Math.abs(to - from) < (decimals > 0 ? 10 ** -decimals / 2 : 0.5)) {
      displayRef.current = to;
      setDisplay(to);
      return undefined;
    }
    const started = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - started) / duration);
      const eased = 1 - (1 - t) ** 3;
      const next = from + (to - from) * eased;
      displayRef.current = next;
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else {
        displayRef.current = to;
        setDisplay(to);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [hasTarget, targetNum, duration, decimals]);

  if (!hasTarget) return null;
  return decimals > 0 ? Number(display.toFixed(decimals)) : Math.round(display);
}

function SmoothMoney({ value, className = "" }) {
  const n = useSmoothNumber(value, { duration: 420, decimals: 0 });
  return (
    <span className={`tabular-nums ${className}`}>
      {n == null ? "—" : `£${Num(n)}`}
    </span>
  );
}

function SmoothPct({ value, digits = 2, className = "", suffix = "%" }) {
  const n = useSmoothNumber(value, { duration: 420, decimals: digits });
  return (
    <span className={`tabular-nums ${className}`}>
      {n == null ? "—" : `${Number(n).toFixed(digits)}${suffix}`}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const asNumber = (...values) => {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const fieldValue = (row, ...keys) => {
  for (const key of keys) {
    const direct = row?.[key];
    if (direct != null && direct !== "") return direct;
    const source = row?.sourceProduct?.[key] ?? row?.rawProduct?.[key] ?? row?.product?.[key];
    if (source != null && source !== "") return source;
  }
  return undefined;
};

const getMinServiceFeeAmount = (row, facilityAmount) => {
  const raw = asNumber(
    fieldValue(row, "miniServiceFeePct", "minServiceFee", "min_service_fee", "minimumServiceFee")
  );
  const baseAmount = Number(facilityAmount);
  if (raw <= 0) return 0;
  if (raw < 100) {
    if (!Number.isFinite(baseAmount) || baseAmount <= 0) return 0;
    return Math.round((baseAmount * raw) / 100);
  }
  return Math.round(raw);
};

const getAdditionalLoanValue = (facilityAmount, additionalBorrowingNum, additionalBorrowingType) => {
  const base = Number(facilityAmount);
  const extraRaw = Number(additionalBorrowingNum);
  if (!Number.isFinite(base) || base <= 0) return 0;
  if (!Number.isFinite(extraRaw) || extraRaw <= 0) return base;
  const extra = additionalBorrowingType === "percent" ? (base * extraRaw) / 100 : extraRaw;
  return Math.round(base + extra);
};

const monthlyPayment = (principal, annualRateDecimal, months) => {
  if (!Number.isFinite(principal) || principal <= 0 || !Number.isFinite(months) || months <= 0) return 0;
  if (!annualRateDecimal) return principal / months;
  const monthlyRate = annualRateDecimal / 12;
  return principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
};

/** Sum of interest paid in the first 12 months of a capital-repayment loan. */
const calcFirst12MonthsInterest = (principal, annualRateDecimal, termMonths) => {
  const p = Number(principal);
  const months = Number(termMonths);
  if (!Number.isFinite(p) || p <= 0 || !Number.isFinite(months) || months <= 0) return 0;
  if (!annualRateDecimal) return 0;

  const monthlyRate = annualRateDecimal / 12;
  const payment = monthlyPayment(p, annualRateDecimal, months);
  const horizon = Math.min(12, months);
  let balance = p;
  let totalInterest = 0;

  for (let i = 0; i < horizon; i += 1) {
    const interest = balance * monthlyRate;
    totalInterest += interest;
    balance -= payment - interest;
  }

  return Math.round(totalInterest);
};

const recalculateAdditionalBorrowing = (abCalc, row) => {
  const repaymentTypeRaw = String(
    fieldValue(row, "io_or_capital_repayment", "ioOrCapitalRepayment") ?? ""
  ).trim();
  const isCapitalRepayment =
    repaymentTypeRaw === "Capital Repayment" || abCalc?.repaymentType === "Capital & Interest";
  if (!isCapitalRepayment) return null;

  const abPrincipal = asNumber(
    abCalc?.abPrincipal,
    abCalc?.principal,
    fieldValue(row, "additionalBorrowingAmount", "additional_borrowing_amount")
  );
  const termMonths = asNumber(
    abCalc?.termMonths,
    fieldValue(row, "abTermMonths", "term_months", "contractTermMonths", "contract_term_months")
  );
  const interestRatePct = asNumber(
    abCalc?.interestRatePct,
    fieldValue(row, "interestRatePct", "interest_rate", "interestRate")
  );
  const interestRateDecimal = apiPercentToDecimal(interestRatePct);
  // AB arrangement fee only — never fall back to product arrangement_fee
  const arrangementFeePct = asNumber(
    fieldValue(row, "abArrangementFeePct", "abArrangementFee", "arrangementFeeTerm", "arrangement_fee_term"),
    abCalc?.arrangementFeePct,
    row?._raw?.arrangement_fee_term
  );
  const arrangementFeeRate = apiPercentToDecimal(arrangementFeePct);
  const arrangementFee = Math.round(abPrincipal * arrangementFeeRate);
  const monthlyRepayment = monthlyPayment(abPrincipal, interestRateDecimal, termMonths);
  const totalRepayable = (monthlyRepayment * termMonths) + arrangementFee;
  const costOverTerm = totalRepayable - abPrincipal;
  const first12MonthsInterest = calcFirst12MonthsInterest(abPrincipal, interestRateDecimal, termMonths);

  return {
    ...abCalc,
    abPrincipal: Math.round(abPrincipal),
    interestRatePct,
    repaymentType: "Capital & Interest",
    termMonths,
    arrangementFeePct,
    arrangementFee,
    annualInterest: Math.round(abPrincipal * interestRateDecimal),
    monthlyRepayment: Math.round(monthlyRepayment),
    totalRepayable: Math.round(totalRepayable),
    costOverTerm: Math.round(costOverTerm),
    first12MonthsInterest,
  };
};

const getProductAdditionalLoanValue = (row, additionalBorrowingNum, additionalBorrowingType) => {
  const base = Number(row?.netAdvance);
  const extraRaw = Number(additionalBorrowingNum);
  if (!Number.isFinite(base) || base <= 0) return 0;
  if (!Number.isFinite(extraRaw) || extraRaw <= 0) return base;
  const extra = additionalBorrowingType === "percent" ? (base * extraRaw) / 100 : extraRaw;
  return Math.round(base + extra);
};

const apiPercentToDecimal = (...values) => {
  const raw = asNumber(...values);
  if (!raw) return 0;
  if (Math.abs(raw) > 100) return 0;
  return raw / 100;
};

/** Facility required + 10%, rounded up to the nearest £10,000. */
const calcReviewLimit = (facilityAmount) => {
  const facility = Number(facilityAmount);
  if (!Number.isFinite(facility) || facility <= 0) return null;
  const withTenPct = facility + facility * 0.1;
  return Math.ceil(withTenPct / 10000) * 10000;
};

const BASE_RATE_PCT = 3.75;

/**
 * Shared mini calculator for existing-facility matching: raw IP / discount / service /
 * min fee + facility → net advance, APR, and cost over a 1-year term.
 */
function computeMiniCalculatorEconomics({
  facilityAmount,
  ipPct,
  discountPct,
  serviceFeePct,
  miniServiceFeeAmount,
}) {
  const facility = Math.max(0, Math.round(Number(facilityAmount) || 0));
  const ip = Math.min(100, Math.max(0, Number(ipPct) || 0));
  const discount = Math.min(100, Math.max(0, Number(discountPct) || 0));
  const service = Math.min(100, Math.max(0, Number(serviceFeePct) || 0));
  const miniFee = Math.max(0, Number(miniServiceFeeAmount) || 0);
  const netAdvance = Math.max(1, Math.round(facility * (ip / 100)) || facility || 1);
  const termYears = 1;
  const discountFeeAmount = netAdvance * ((discount + BASE_RATE_PCT) / 100) * termYears;
  const serviceFeeAmount = Math.max(facility * (service / 100), miniFee) * termYears;
  const costOverProductTerm = Math.round(discountFeeAmount + serviceFeeAmount + serviceFeeAmount * 0.2);
  const cost = Math.max(costOverProductTerm, 1);
  const ratePct = Math.round(((cost / netAdvance) * 100) * 100) / 100;

  return {
    ipPct: ip,
    discountPct: discount,
    serviceFeePct: service,
    miniServiceFeeAmount: Math.round(miniFee),
    netAdvance,
    ratePct,
    costOverProductTerm: cost,
  };
}

function getProductRawFacilityInputs(row, facilityAmount) {
  return {
    ipPct: normalizePercentScale(
      asNumber(
        fieldValue(row, "invoicePurchasePct", "ip_percent"),
        row?._raw?.ip_percent,
        row?.sourceProduct?.ip_percent
      )
    ) ?? 0,
    discountPct: asNumber(
      fieldValue(row, "discountRatePct", "discountRatePercent", "discount_rate_percent"),
      row?._raw?.discount_rate_percent,
      row?.sourceProduct?.discount_rate_percent
    ),
    serviceFeePct: asNumber(
      fieldValue(row, "annualCostPct", "serviceFeePct", "service_fee_percent"),
      row?._raw?.service_fee_percent,
      row?.sourceProduct?.service_fee_percent
    ),
    miniServiceFeeAmount: getMinServiceFeeAmount(row, facilityAmount),
  };
}

/** Build a synthetic “existing facility” row from the user’s current-facility inputs. */
function buildExistingComparisonRow({
  facilityAmount,
  ipPct,
  discountPct,
  serviceFeePct,
  miniServiceFeeAmount,
}) {
  const calc = computeMiniCalculatorEconomics({
    facilityAmount,
    ipPct,
    discountPct,
    serviceFeePct,
    miniServiceFeeAmount,
  });
  const facility = Math.max(0, Math.round(Number(facilityAmount) || 0));

  return {
    id: "existing-facility",
    lenderName: "Your existing facility",
    invoicePurchasePct: calc.ipPct,
    discountRatePct: calc.discountPct,
    ratePct: calc.ratePct,
    headlineAmount: facility || calc.netAdvance,
    termMonths: 12,
    dpAllowed: "Yes",
    additionalBorrowing: true,
    annualCostPct: Math.round(calc.serviceFeePct * 100) / 100,
    totalCostPct: Math.round((calc.serviceFeePct + calc.discountPct) * 100) / 100,
    netAdvance: calc.netAdvance,
    miniServiceFeePct: calc.miniServiceFeeAmount,
    costOverProductTerm: calc.costOverProductTerm,
  };
}

/**
 * Pick catalogue product closest to the user’s existing facility by displayed APR only.
 * Uses each card’s `ratePct` (same figure shown in the UI), not a separate mini-calc APR.
 */
function findBestMatchingProductId(existing, products) {
  if (!existing || !products?.length) return null;

  const existingApr = Number(existing.ratePct);
  if (!Number.isFinite(existingApr)) return null;

  let bestId = null;
  let bestDiff = Infinity;

  for (const p of products) {
    const productApr = Number(p.ratePct);
    if (!Number.isFinite(productApr)) continue;

    const diff = Math.abs(productApr - existingApr);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestId = p.id;
    }
  }

  return bestId;
}

function InvoiceFacilitySnapshot({ row, facilityAmount }) {
  const aprPct = Number(row.ratePct).toFixed(2);
  const advanceShown = row.netAdvance;
  const feeAmount = row?.id === "existing-facility"
    ? Number(row.miniServiceFeePct)
    : getMinServiceFeeAmount(row, facilityAmount ?? row?.netAdvance);
  const feeShown = Number.isFinite(feeAmount) ? String(Math.round(feeAmount)) : "—";
  const serviceFeeShown =
    typeof row.annualCostPct === "number" && Number.isFinite(row.annualCostPct)
      ? Number(row.annualCostPct).toFixed(2)
      : "—";
  const costTermShown = row.costOverProductTerm;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="px-2 pt-2 min-w-0 overflow-x-auto overflow-y-hidden hide-scrollbar touch-pan-x overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="w-max max-w-none">
          <InvoiceProductChips
            invoicePurchasePct={row.invoicePurchasePct}
            dpAllowed={row.dpAllowed}
            additionalLoanAmount={null}
            maxAbAmount={null}
            showAdditionalBorrowingChip={false}
            reviewLimitAmount={calcReviewLimit(facilityAmount)}
            compact
            nowrap
          />
        </div>
      </div>
      <div className="px-2 pb-2 pt-1.5">
        <div className="grid grid-cols-2 gap-x-1 gap-y-1.5">
          <div className="col-span-2 flex flex-col items-center justify-center rounded-lg bg-sky-500 px-2 py-2 text-white shadow-sm">
            <div className="text-[11px] font-semibold leading-tight">APR</div>
            <div className="text-[28px] font-extrabold leading-none tabular-nums tracking-tight">{aprPct}%</div>
          </div>
          <div className="flex min-w-0 flex-col items-center text-center">
            <div className="text-[10px] font-medium leading-tight text-slate-600">Net Advance</div>
            <div className="text-[16px] font-extrabold leading-tight tabular-nums text-slate-900 break-words">
              £{Num(advanceShown)}
            </div>
          </div>
          <div className="flex min-w-0 flex-col items-center text-center">
            <div className="text-[10px] font-medium leading-tight text-slate-600">Min. fee</div>
            <div className="text-[16px] font-extrabold tabular-nums text-slate-900">£{feeShown}</div>
          </div>
          <div className="col-span-2 flex flex-col items-center border-t border-slate-100 pt-1.5 text-center">
            <div className="text-[10px] font-medium leading-tight text-slate-600">Service fee</div>
            <div className="text-[16px] font-extrabold tabular-nums text-slate-900">{serviceFeeShown}%</div>
          </div>
          <div className="col-span-2 flex flex-col items-center border-t border-slate-100 pt-1.5 text-center">
            <div className="text-[10px] font-medium leading-tight text-slate-600">Cost over term</div>
            <div className="text-[16px] font-extrabold leading-tight tabular-nums text-slate-900 break-words">
              £{Num(costTermShown)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExistingFacilityComparisonPanel({ row, facilityAmount, onClose }) {
  return (
    <div className="pointer-events-auto max-h-[min(92vh,780px)] w-full overflow-y-auto rounded-2xl shadow-2xl shadow-amber-900/15">
      <div className="rounded-2xl border-2 border-amber-300 bg-yellow-100 ring-1 ring-amber-200/70">
        <div className="flex items-start justify-between gap-2 rounded-t-[14px] border-b border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/90 px-3 py-2.5">
          <div className="min-w-0 pr-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-800/90">
              Your existing facility
            </div>
            <div className="text-sm font-bold leading-snug text-amber-950">Compare against catalogue</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide comparison card"
            className="flex-shrink-0 rounded-xl border border-amber-300/80 bg-white p-1.5 text-amber-900 hover:bg-amber-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="bg-yellow-50/90 p-2">
          <InvoiceFacilitySnapshot row={row} facilityAmount={facilityAmount} />
        </div>
        <p className="border-t border-slate-100 px-3 pb-2.5 pt-2 text-[10px] leading-snug text-slate-500">
          Highlighted product is the closest match to your headline terms and all-in cost.
        </p>
      </div>
    </div>
  );
}

const calculateAdjustedProductCosts = (row, { facilityAmount, turnoverAmount, utilisationPct }) => {
  const facility = Number(facilityAmount);
  const turnover = Number(turnoverAmount);
  if (!Number.isFinite(facility) || facility <= 0) return row;

  const utilisation = Math.min(Math.max(Number(utilisationPct) || 0, 0), 100) / 100;
  const netAdvance = Number(row?.netAdvance);
  const discountBase = Number.isFinite(netAdvance) && netAdvance > 0 ? netAdvance : facility;
  const termYears = 1;

  const productDiscountPct = asNumber(
    fieldValue(row, "discountRatePct", "discountRatePercent", "discount_rate_percent", "discountPct", "discountRate"),
    row?._raw?.discount_rate_percent,
    row?.sourceProduct?.discount_rate_percent
  );
  const discountRate = apiPercentToDecimal(productDiscountPct + BASE_RATE_PCT);
  const effectiveDiscountRate = discountRate * utilisation;
  const serviceFeeRate = apiPercentToDecimal(
    fieldValue(row, "serviceFeePct", "serviceFeePercent", "service_fee_percent", "serviceFee"),
    row?.annualCostPct
  );
  // Facility IF arrangement fee only — never use arrangement_fee_term (that is AB-only)
  const arrangementFeeRate = apiPercentToDecimal(
    fieldValue(row, "arrangementFeePct", "arrangementFeePercent", "arrangement_fee", "arrangementPct", "arrangementFee")
  );
  const renewalFeeRate = apiPercentToDecimal(
    fieldValue(row, "renewalFeePct", "renewalFeePercent", "renewal_fee", "renewalPct", "renewalFee")
  );

  const discountFeeAmount = discountBase * effectiveDiscountRate * termYears;
  const rawAnnualServiceFeeAmount = Number.isFinite(turnover) && turnover > 0
    ? turnover * serviceFeeRate
    : 0;
  const minServiceFeeAmount = getMinServiceFeeAmount(row, facility);
  const serviceFeeAmount = Math.max(rawAnnualServiceFeeAmount, minServiceFeeAmount) * termYears;
  const reviewLimit = calcReviewLimit(facility) ?? facility;
  const arrangementFeeAmount = reviewLimit * arrangementFeeRate;
  const renewalFeeAmount = facility * renewalFeeRate;
  const fixedFeeAmount = asNumber(fieldValue(row, "chapsFee", "chaps"))
    + asNumber(fieldValue(row, "docFee", "doc_fee", "documentationFee"))
    + asNumber(fieldValue(row, "auditFee", "audit_fee"))
    + asNumber(fieldValue(row, "refactoringCharge", "refactoring_charge", "refactoringFee"));

  const serviceFeeVat = serviceFeeAmount * 0.20;
  const ifBaseCost = Math.round(
    discountFeeAmount + serviceFeeAmount + serviceFeeVat + arrangementFeeAmount + fixedFeeAmount
  );
  const abCalc = row?.abCalc
    ? (recalculateAdditionalBorrowing(row.abCalc, row) ?? row.abCalc)
    : row?.abCalc;
  const abFirst12Interest = abCalc?.first12MonthsInterest ?? 0;
  const costOverProductTerm = ifBaseCost + abFirst12Interest;
  const vatAmount = serviceFeeVat;
  const ratePct = Math.round(((costOverProductTerm / discountBase) * 100) * 100) / 100;

  return {
    ...row,
    abCalc,
    ifBaseCost,
    // Keep catalogue discount for existing-facility matching; `ratePct` is display APR.
    discountRatePct: productDiscountPct,
    ratePct,
    costOverProductTerm,
    utilisationPct: Math.round(utilisation * 100),
    utilisedFacility: Math.round(facility * utilisation),
    discountFeeAmount: Math.round(discountFeeAmount),
    serviceFeeAmount: Math.round(serviceFeeAmount),
    arrangementFeeAmount: Math.round(arrangementFeeAmount),
    renewalFeeAmount: Math.round(renewalFeeAmount),
    fixedFeeAmount: Math.round(fixedFeeAmount),
    vatAmount: Math.round(vatAmount),
  };
};

// ─── UI primitives ────────────────────────────────────────────────────────────
const SleepyPolarBearEmptyState = () => (
  <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
    <div className="relative w-56 h-40">
      <img src="/sleepy_bear.png" alt="Sleepy polar bear resting" className="h-full w-full object-contain mx-auto" />
      <span className="snore-z text-lg left-[28%] top-[45%]" style={{ animationDelay: "0s" }}>Z</span>
      <span className="snore-z text-xl left-[22%] top-[38%]" style={{ animationDelay: "1s" }}>Z</span>
      <span className="snore-z text-2xl left-[16%] top-[31%]" style={{ animationDelay: "2s" }}>Z</span>
    </div>
    <p className="mt-4 text-base font-semibold text-slate-800">No matching products to show.</p>
    <p className="mt-1 text-sm text-slate-500 max-w-sm">Try adjusting the filters or loan details to see more options.</p>
  </div>
);

const DetailRow = ({ label, value, strong }) => (
  <div className="flex items-start justify-between gap-3 min-w-0 w-full">
    <dt className="text-xs md:text-sm text-slate-600 min-w-0 flex-1 break-words leading-snug">{label}</dt>
    <dd
      className={
        "text-xs md:text-sm text-right shrink-0 whitespace-nowrap tabular-nums " +
        (strong ? "font-semibold text-slate-900" : "text-slate-900")
      }
    >
      {value}
    </dd>
  </div>
);

const InputShell = ({ label, children, labelClassName }) => (
  <label className="block">
    <div className={`mb-1 ${labelClassName ?? "text-slate-700 text-[13px]"}`}>{label}</div>
    {children}
  </label>
);

const Select = ({ children, className = "", ...rest }) => (
  <div className="relative group transform-gpu">
    <select
      className={"w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 pr-10 py-3 text-[16px] md:text-[17px] ring-1 ring-slate-200 transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-sky-300 focus:border-sky-300 " + className}
      {...rest}
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2.5 top-3.5 h-5 w-5 text-sky-500 transition-transform duration-150 group-focus-within:rotate-180" />
  </div>
);

/** Custom sector picker — each option has an info tip with a one-line sector blurb. */
function SectorSelect({ value, options, onChange, className = "" }) {
  const [open, setOpen] = React.useState(false);
  const [openInfoSector, setOpenInfoSector] = React.useState("");
  const rootRef = React.useRef(null);
  const listRef = React.useRef(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 280 });

  const closeMenu = React.useCallback(() => {
    setOpen(false);
    setOpenInfoSector("");
  }, []);

  const updatePos = React.useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, 240);
    let left = r.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - 8 - width);
    }
    setPos({ top: r.bottom + 4, left, width });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    updatePos();
    const onScrollOrResize = () => updatePos();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePos]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || listRef.current?.contains(t)) return;
      // Keep open while interacting with a portaled InfoTip bubble.
      if (t?.closest?.('[data-role="info-tip"], [role="dialog"]')) return;
      closeMenu();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, closeMenu]);

  const sectors = (options || []).filter((o) => String(o || "").trim() !== "");
  const display = value
    ? formatInvoiceFinanceSectorLabel(value)
    : "Select";

  const menu = open
    ? createPortal(
        <div
          ref={listRef}
          role="listbox"
          aria-label="Sector"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: pos.width,
            zIndex: 10000,
          }}
          className="max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-black/5"
        >
          <button
            type="button"
            role="option"
            aria-selected={!value}
            className={
              "flex w-full items-center gap-2 px-3 py-2.5 text-left text-[15px] hover:bg-sky-50 " +
              (!value ? "bg-sky-50 text-sky-800 font-semibold" : "text-slate-700")
            }
            onClick={() => {
              onChange("");
              closeMenu();
            }}
          >
            Select
          </button>
          {sectors.map((sector) => {
            const label = formatInvoiceFinanceSectorLabel(sector);
            const blurb = INVOICE_FINANCE_SECTOR_BLURBS[sector] || `Products for the ${label} sector.`;
            const selected = sector === value;
            return (
              <div
                key={sector}
                role="option"
                aria-selected={selected}
                className={
                  "flex w-full items-center gap-1.5 px-2 py-1.5 " +
                  (selected ? "bg-sky-50" : "hover:bg-slate-50")
                }
              >
                <button
                  type="button"
                  className={
                    "min-w-0 flex-1 rounded-lg px-1.5 py-1.5 text-left text-[15px] " +
                    (selected ? "text-sky-800 font-semibold" : "text-slate-800")
                  }
                  onClick={() => {
                    onChange(sector);
                    closeMenu();
                  }}
                >
                  <span className="block truncate">{label}</span>
                </button>
                <span
                  className="shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <InfoTip
                    text={blurb}
                    labelForAria={label}
                    zIndex={10050}
                    open={openInfoSector === sector}
                    onOpenChange={(next) => setOpenInfoSector(next ? sector : "")}
                  />
                </span>
              </div>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={rootRef} className={"relative transform-gpu " + (open ? "z-[60]" : "")}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            if (!next) setOpenInfoSector("");
            return next;
          });
        }}
        className={
          "group flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 pr-3 py-3 text-left text-[16px] md:text-[17px] ring-1 ring-slate-200 transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-sky-300 focus:border-sky-300 " +
          className
        }
      >
        <span className={value ? "text-slate-900 truncate" : "text-slate-400 truncate"}>{display}</span>
        <ChevronDown
          className={
            "h-5 w-5 shrink-0 text-sky-500 transition-transform duration-150 " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {menu}
    </div>
  );
}

const ToggleSwitch = ({ checked, onChange, id, label }) => (
  <div className="flex items-center justify-between gap-3 py-1">
    <span className="text-[15px] md:text-[16px] font-semibold text-slate-900">{label}</span>
    <button
      type="button" id={id} role="switch" aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 ${checked ? "bg-sky-500" : "bg-slate-300"}`}
    >
      <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </div>
);

function InfoTip({ text, labelForAria, open: openProp, onOpenChange, zIndex = 10050, icon: IconComp = Info }) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? Boolean(openProp) : uncontrolledOpen;
  const setOpen = React.useCallback((next) => {
    const value = typeof next === "function" ? next(open) : next;
    if (!isControlled) setUncontrolledOpen(value);
    onOpenChange?.(value);
  }, [isControlled, onOpenChange, open]);

  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0, width: 280 });

  const updatePos = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxW = 280;
    const w = Math.min(maxW, window.innerWidth - 16);
    let left = r.left;
    if (left + w > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - w);
    setPos({ top: r.bottom + 6, left, width: w });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    updatePos();
    const onScrollOrResize = () => updatePos();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePos]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, setOpen]);

  const panel = open && createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-live="polite"
      data-role="info-tip"
      style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex }}
      className="rounded-xl border border-slate-200 bg-white p-3 text-[13px] leading-snug text-slate-700 shadow-lg ring-1 ring-black/5"
    >{text}</div>,
    document.body
  );

  return (
    <>
      <button ref={btnRef} type="button"
        className="shrink-0 rounded-full p-0.5 text-sky-600 hover:bg-sky-100 hover:text-sky-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
        aria-label={labelForAria ? `About ${labelForAria}` : "More information"}
        aria-expanded={open} aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        <IconComp className="h-4 w-4" strokeWidth={2} />
      </button>
      {panel}
    </>
  );
}

const GradientChip = ({ children }) => (
  <span className="inline-flex rounded-full p-[1px] bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500">
    <span className="px-3 py-1 rounded-full bg-white text-slate-900 text-[12px] font-semibold">{children}</span>
  </span>
);

const GreenTransparentChip = ({ children, compact }) => (
  <span className={"inline-flex shrink-0 items-center rounded-full border border-green-200 bg-green-50 text-green-700 font-semibold whitespace-nowrap " + (compact ? "px-1.5 py-0.5 text-[9px] leading-tight" : "px-3 py-1 text-[12px]")}>
    {children}
  </span>
);

const RedTransparentChip = ({ children, compact }) => (
  <span className={"inline-flex shrink-0 items-center rounded-full border border-red-200 bg-red-50 text-red-700 font-semibold whitespace-nowrap " + (compact ? "px-1.5 py-0.5 text-[9px] leading-tight" : "px-3 py-1 text-[12px]")}>
    {children}
  </span>
);

const GreenTick = ({ compact }) => (
  <span
    className={
      "inline-flex shrink-0 items-center justify-center rounded-full bg-green-500 text-white " +
      (compact ? "h-2.5 w-2.5" : "h-4 w-4")
    }
    aria-hidden
  >
    <Check className={compact ? "h-1.5 w-1.5" : "h-2.5 w-2.5"} strokeWidth={3} />
  </span>
);

function InvoiceProductChips({
  invoicePurchasePct, dpAllowed, additionalLoanAmount, maxAbAmount,
  showAdditionalBorrowingChip, reviewLimitAmount, compact, nowrap = false,
}) {
  const ip = typeof invoicePurchasePct === "number" && Number.isFinite(invoicePurchasePct) ? Math.round(invoicePurchasePct) : null;
  const C = compact
    ? ({ children }) => (
        <span className="inline-flex shrink-0 rounded-full p-[1px] bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500">
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white text-slate-900 text-[9px] font-semibold leading-none whitespace-nowrap">
            {children}
          </span>
        </span>
      )
    : GradientChip;
  const gap = compact ? "gap-1" : "gap-2";
  const hasMaxAbCap = maxAbAmount != null && Number(maxAbAmount) > 0;
  const exceedsMaxAb = showAdditionalBorrowingChip
    && additionalLoanAmount != null
    && hasMaxAbCap
    && additionalLoanAmount > maxAbAmount;
  const unlimitedAb = showAdditionalBorrowingChip && !hasMaxAbCap;
  const withinMaxAb = showAdditionalBorrowingChip
    && additionalLoanAmount != null
    && hasMaxAbCap
    && !exceedsMaxAb;

  return (
    <div className={`flex items-center ${nowrap ? "flex-nowrap shrink-0" : "flex-wrap"} ${gap}`}>
      {ip != null ? <C>{`IP ${ip}%`}</C> : null}
      <C>
        <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
          DP{dpAllowed === "Yes" ? <GreenTick compact={compact} /> : null}
        </span>
      </C>
      {reviewLimitAmount != null ? (
        <C>{`Review limit £${formatReviewLimitChip(reviewLimitAmount)}`}</C>
      ) : null}
      {exceedsMaxAb ? (
        <RedTransparentChip compact={compact}>
          {compact
            ? `Max AB £${formatCompactMoneyChip(maxAbAmount)}`
            : `Max AB: £${Num(maxAbAmount)}`}
        </RedTransparentChip>
      ) : unlimitedAb ? (
        <GreenTransparentChip compact={compact}>
          <span className="inline-flex items-center gap-0.5 whitespace-nowrap">AB: STA<GreenTick compact={compact} /></span>
        </GreenTransparentChip>
      ) : withinMaxAb ? (
        <GreenTransparentChip compact={compact}>
          <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
            {compact
              ? `AB £${formatCompactMoneyChip(additionalLoanAmount)}`
              : `Additional loan £${Num(additionalLoanAmount)}`}
            <GreenTick compact={compact} />
          </span>
        </GreenTransparentChip>
      ) : null}
    </div>
  );
}

const RangeRow = ({
  label, value, onChange, min = 0, max = 100, step = 1, suffix = "%", info, formatValue,
}) => {
  const [inputValue, setInputValue] = React.useState(String(value ?? ""));

  React.useEffect(() => {
    setInputValue(String(value ?? ""));
  }, [value]);

  const clampValue = React.useCallback((n) => {
    if (!Number.isFinite(n)) return min;
    return Math.min(max, Math.max(min, n));
  }, [min, max]);

  const commitInputValue = React.useCallback((raw) => {
    if (raw === "") {
      onChange(min);
      return;
    }
    const next = Number(raw);
    if (!Number.isFinite(next)) {
      onChange(min);
      return;
    }
    onChange(clampValue(next));
  }, [onChange, min, clampValue]);

  return (
    <div className="py-1">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[15px] md:text-[16px] font-semibold text-slate-900">{label}</span>
          {info ? <InfoTip text={info} labelForAria={label} /> : null}
        </div>
        <label className="inline-flex items-center gap-1 shrink-0 text-sm font-semibold text-sky-700 tabular-nums">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={inputValue}
            onChange={(e) => {
              const raw = e.target.value;
              setInputValue(raw);
              if (raw === "") return;
              const next = Number(raw);
              if (!Number.isFinite(next)) return;
              onChange(clampValue(next));
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace") {
                e.preventDefault();
                setInputValue("");
              }
            }}
            onBlur={(e) => {
              commitInputValue(e.target.value);
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") commitInputValue(e.currentTarget.value);
            }}
            className="no-spinner w-14 rounded-md border border-transparent bg-transparent px-1 py-0 text-right text-sm font-semibold text-sky-700 focus:border-sky-200 focus:bg-white focus:ring-1 focus:ring-sky-300 focus:outline-none"
            aria-label={`${label} value`}
          />
          {suffix ? <span>{suffix}</span> : null}
        </label>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none bg-slate-200 accent-sky-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-sky-500 [&::-webkit-slider-thumb]:shadow"
      />
    </div>
  );
};

const INVOICE_SORT_ITEMS = [
  { key: "rate", label: "APR (low to high)" },
  { key: "cost", label: "Cost over term (low to high)" },
  { key: "advance", label: "Net Advance (high to low)" },
  { key: "additional-loan", label: "Additional loan (high to low)" },
  { key: "mini-fee", label: "Min. service fee (low to high)" },
  { key: "ip", label: "Initial Percentage" },
];

const InvoiceSortControl = ({ sortBy, setSortBy }) => {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = INVOICE_SORT_ITEMS.find((i) => i.key === sortBy)?.label ?? "Sort by";

  return (
    <div className="relative inline-block">
      <button ref={btnRef} type="button" onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold shadow-sm hover:shadow md:px-4"
        aria-haspopup="listbox" aria-expanded={open}
      >
        <SlidersHorizontal className="h-4 w-4 text-slate-600" />
        <span className="text-slate-800">Sort By:</span>
        <span className="text-slate-500 font-medium">{current}</span>
      </button>
      {open && (
        <div ref={popRef} className="absolute right-0 z-30 mt-2 w-[300px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl" role="listbox">
          {INVOICE_SORT_ITEMS.map((it) => {
            const active = it.key === sortBy;
            return (
              <button key={it.key} role="option" aria-selected={active}
                onClick={() => { setSortBy(it.key); setOpen(false); }}
                className={"w-full flex items-center justify-between px-4 py-3 text-sm " + (active ? "bg-sky-50 text-sky-800" : "hover:bg-slate-50 text-slate-800")}
              >
                <span>{it.label}</span>
                {active ? <Check className="h-4 w-4" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DEFAULT_INVOICE_FILTERS = {
  creditWorthiness: "Good",
  creditTermsDays: 45,
  // Default 12 months; set 0 for Any, or 24 to narrow.
  contractTermMonths: 12,
  contractualDebtAgreements: false,
  proofOfDebt: false,
  currentShortTermDebt: false,
  selectiveInvoicing: false,
  debtorProtection: false,
  fundingLedgerType: "full",
  hmrcArrears: false,
  previousLiquidatedCo: false,
  directorExperience: true,
  personalGuarantees: true,
  confidentialFacility: true,
  concentrationLimit: 40,
  exportLedger: 30,
  // Construction-only filter ("" = Select / no restriction).
  homeOwner: "",
};

const IF_SOURCING_QS_KEY = "if_sourcing_qs";
const IF_SOURCING_FILTERS_KEY = "if_sourcing_filters";

const DEFAULT_IF_SOURCING_QS = {
  applicationMode: "new",
  sector: "",
  projectedTurnover: "",
  facilityRequired: "",
  additionalBorrowingType: "amount",
  additionalBorrowingValue: "",
  facilityUtilisationPct: 60,
  showExistingFloat: true,
};

function readStoredIfSourcingQs() {
  try {
    const saved = localStorage.getItem(IF_SOURCING_QS_KEY);
    if (!saved) return { ...DEFAULT_IF_SOURCING_QS };
    const parsed = JSON.parse(saved);
    // Existing-facility headline inputs are session-only — never restore from storage.
    const {
      existingIp: _existingIp,
      existingDiscount: _existingDiscount,
      existingServiceFee: _existingServiceFee,
      existingMiniServiceFee: _existingMiniServiceFee,
      ...persistedQs
    } = parsed;
    return {
      ...DEFAULT_IF_SOURCING_QS,
      ...persistedQs,
      // £-only AB input — ignore any previously saved % mode.
      additionalBorrowingType: "amount",
    };
  } catch {
    return { ...DEFAULT_IF_SOURCING_QS };
  }
}

function readStoredIfSourcingFilters() {
  try {
    const saved = localStorage.getItem(IF_SOURCING_FILTERS_KEY);
    if (!saved) return { ...DEFAULT_INVOICE_FILTERS };
    const parsed = JSON.parse(saved);
    const merged = { ...DEFAULT_INVOICE_FILTERS, ...parsed };
    for (const key of Object.keys(DEFAULT_INVOICE_FILTERS)) {
      const v = merged[key];
      if (v === undefined || v === null) merged[key] = DEFAULT_INVOICE_FILTERS[key];
    }
    return merged;
  } catch {
    return { ...DEFAULT_INVOICE_FILTERS };
  }
}

function countActiveInvoiceFilters(filters, facilityUtilisationPct, sector = "") {
  let count = 0;
  const d = DEFAULT_INVOICE_FILTERS;
  if (filters.creditWorthiness !== d.creditWorthiness) count += 1;
  if (Number(filters.creditTermsDays) !== d.creditTermsDays) count += 1;
  if (Number(filters.contractTermMonths) !== d.contractTermMonths) count += 1;
  if (!!filters.contractualDebtAgreements !== d.contractualDebtAgreements) count += 1;
  if (!!filters.proofOfDebt !== d.proofOfDebt) count += 1;
  if (!!filters.currentShortTermDebt !== d.currentShortTermDebt) count += 1;
  if (!!filters.selectiveInvoicing !== d.selectiveInvoicing) count += 1;
  if (!!filters.debtorProtection !== d.debtorProtection) count += 1;
  if ((filters.fundingLedgerType || "") !== d.fundingLedgerType) count += 1;
  if (!!filters.hmrcArrears !== d.hmrcArrears) count += 1;
  if (!!filters.previousLiquidatedCo !== d.previousLiquidatedCo) count += 1;
  if (!!filters.directorExperience !== d.directorExperience) count += 1;
  if (!!filters.personalGuarantees !== d.personalGuarantees) count += 1;
  if (!!filters.confidentialFacility !== d.confidentialFacility) count += 1;
  if (Number(filters.concentrationLimit) !== d.concentrationLimit) count += 1;
  if (Number(filters.exportLedger) !== d.exportLedger) count += 1;
  if (Number(facilityUtilisationPct) !== 60) count += 1;
  if (
    isInvoiceFinanceConstructionSector(sector) &&
    (filters.homeOwner || "") !== d.homeOwner
  ) {
    count += 1;
  }
  return count;
}

function InvoiceFiltersBody({
  filters, setF, facilityUtilisationPct, setFacilityUtilisationPct,
  applicationMode, setApplicationMode,
  sector,
  existingIp, setExistingIp,
  existingDiscount, setExistingDiscount,
  existingServiceFee, setExistingServiceFee,
  existingMiniServiceFee, setExistingMiniServiceFee,
  onClearExistingFacility,
  normalizeNumberInput,
  existingComparisonReady, showExistingFloat, setShowExistingFloat,
  onReset,
}) {
  const hasExistingFacilityInput = [existingIp, existingDiscount, existingServiceFee, existingMiniServiceFee]
    .some((value) => String(value || "").trim() !== "");
  const showHomeOwnerFilter = isInvoiceFinanceConstructionSector(sector);
  return (
    <div className="space-y-4">
      {typeof setApplicationMode === "function" ? (
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="text-slate-900 font-semibold text-[14px] md:text-[15px] shrink-0">Client Type</div>
          <div className="inline-flex w-fit rounded-full bg-slate-100 p-1 ring-1 ring-slate-200" role="group" aria-label="Client Type">
            {["new", "existing"].map((mode) => (
              <button key={mode} type="button" onClick={() => setApplicationMode(mode)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors capitalize ${applicationMode === mode ? "bg-sky-600 text-white shadow" : "text-slate-600 hover:bg-slate-200"}`}
              >{mode}</button>
            ))}
          </div>
        </div>
      ) : null}

      {applicationMode === "existing" && (
        <div className="border-t border-slate-200 pt-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="text-slate-900 font-semibold text-[14px] md:text-[15px]">Your current facility</div>
              <InfoTip
                labelForAria="Your current facility"
                text="Enter the 4 parameters below of your existing facility to see where you stand in the market."
              />
            </div>
            {hasExistingFacilityInput ? (
              <button
                type="button"
                onClick={onClearExistingFacility}
                className="shrink-0 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
              >
                Clear all
              </button>
            ) : null}
          </div>
          {[["IP (%)", existingIp, setExistingIp], ["Discount (%)", existingDiscount, setExistingDiscount], ["Service Fee (%)", existingServiceFee, setExistingServiceFee]].map(([label, val, setter]) => (
            <InputShell key={label} label={label} labelClassName="text-slate-900 font-semibold text-[14px] md:text-[15px]">
              <input type="number" min={0} max={100} step="0.01" inputMode="decimal" placeholder="0"
                className="no-spinner w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] ring-1 ring-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-300"
                value={val}
                onChange={(e) => { const next = normalizeNumberInput(e.target.value, { allowDecimal: true }); if (next == null) return; setter(next); }}
              />
            </InputShell>
          ))}
          <InputShell label="Min. Service Fee (£)" labelClassName="text-slate-900 font-semibold text-[14px] md:text-[15px]">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-800 text-[15px]">£</span>
              <input type="text" inputMode="decimal" placeholder="0"
                className="no-spinner w-full rounded-xl border border-slate-200 bg-white pl-8 pr-3 py-2.5 text-[15px] ring-1 ring-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-300"
                value={existingMiniServiceFee}
                onChange={(e) => { const next = normalizeNumberInput(e.target.value, { allowDecimal: true }); if (next == null) return; setExistingMiniServiceFee(next); }}
              />
            </div>
          </InputShell>
          {existingComparisonReady && !showExistingFloat ? (
            <button
              type="button"
              onClick={() => setShowExistingFloat(true)}
              className="w-full rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
            >
              Show your existing facility card
            </button>
          ) : null}
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 space-y-1">
        <div className="text-slate-900 font-semibold text-[14px] md:text-[15px] mb-2">Eligibility</div>
        <RangeRow
          label="Facility utilisation"
          value={facilityUtilisationPct}
          min={0}
          max={100}
          onChange={setFacilityUtilisationPct}
          info="Applied to discount fee only. Service fee remains based on turnover, subject to the minimum service fee."
        />
        <RangeRow label="Credit terms (days)" value={filters.creditTermsDays} max={180} suffix="" onChange={(v) => setF("creditTermsDays", v)} />
        <RangeRow label="Contract term (months)" value={filters.contractTermMonths} min={0} max={36} suffix="" formatValue={(v) => (v === 0 ? "Any" : String(v))} onChange={(v) => setF("contractTermMonths", v)} />
        <CreditSegment value={filters.creditWorthiness} onChange={(v) => setF("creditWorthiness", v)} />
        <ToggleSwitch id="contractual-debt" label="Contractual debt / agreements in place" checked={filters.contractualDebtAgreements} onChange={(v) => setF("contractualDebtAgreements", v)} />
        <ToggleSwitch id="proof-of-debt" label="Proof of debt" checked={filters.proofOfDebt} onChange={(v) => setF("proofOfDebt", v)} />
        <ToggleSwitch id="short-term-debt" label="Current short term debt" checked={filters.currentShortTermDebt} onChange={(v) => setF("currentShortTermDebt", v)} />
        <ToggleSwitch id="selective-invoicing" label="Selective invoicing" checked={filters.selectiveInvoicing} onChange={(v) => setF("selectiveInvoicing", v)} />
        <ToggleSwitch id="debtor-protection" label="Debtor protection" checked={filters.debtorProtection} onChange={(v) => setF("debtorProtection", v)} />
        <div>
          <div className="text-slate-900 font-semibold text-[15px] md:text-[16px] mb-2">Part fund / fully funded ledger</div>
          <div className="flex flex-wrap gap-1.5">
            {[["Part", "part"], ["Full", "full"]].map(([label, val]) => (
              <button key={label} type="button" onClick={() => setF("fundingLedgerType", val)}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold border transition-colors ${(filters.fundingLedgerType || "") === val ? "border-sky-400 bg-sky-100 text-sky-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              >{label}</button>
            ))}
          </div>
        </div>
        {showHomeOwnerFilter ? (
          <InputShell label="Home owner" labelClassName="text-slate-900 font-semibold text-[14px] md:text-[15px]">
            <Select value={filters.homeOwner || ""} onChange={(e) => setF("homeOwner", e.target.value)} className="!py-2.5 !text-[15px]">
              <option value="">Select</option>
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </Select>
          </InputShell>
        ) : null}
        <ToggleSwitch id="hmrc" label="HMRC arrears" checked={filters.hmrcArrears} onChange={(v) => setF("hmrcArrears", v)} />
        <ToggleSwitch id="prev-liq" label="Previous liquidated co." checked={filters.previousLiquidatedCo} onChange={(v) => setF("previousLiquidatedCo", v)} />
        <ToggleSwitch id="dir-exp" label="Director experience" checked={filters.directorExperience} onChange={(v) => setF("directorExperience", v)} />
        <ToggleSwitch id="pg" label="Personal guarantees" checked={filters.personalGuarantees} onChange={(v) => setF("personalGuarantees", v)} />
        <ToggleSwitch id="cid" label="Confidential facility" checked={filters.confidentialFacility} onChange={(v) => setF("confidentialFacility", v)} />
      </div>

      <div className="border-t border-slate-200 pt-3 space-y-3">
        <RangeRow label="Concentration limit" value={filters.concentrationLimit} onChange={(v) => setF("concentrationLimit", v)}
          info="Caps how much of your ledger can sit with one debtor—lenders use this to limit single-name risk." />
        <RangeRow label="Export ledger" value={filters.exportLedger} onChange={(v) => setF("exportLedger", v)}
          info="Share of overseas or export invoices you want included when matching facilities." />
      </div>

      <button type="button"
        className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-xl border border-sky-300 text-sky-700 font-semibold px-4 py-2.5 text-[15px] transition-colors hover:bg-sky-50"
        onClick={onReset}
      >Reset all filters</button>
    </div>
  );
}

function MobileFiltersDrawer({
  open, onClose, productCount, onShow, onClearAll, filtersBody,
}) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 md:hidden" onClick={onClose} aria-hidden />
      <div
        id="invoice-mobile-filters-drawer"
        className="fixed inset-x-0 bottom-0 z-[61] flex max-h-[90vh] flex-col rounded-t-2xl bg-white shadow-2xl md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close filters">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0 bg-gray-50">
          {filtersBody}
        </div>
        <div className="shrink-0 border-t border-slate-200 bg-white p-4 flex gap-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onClearAll}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={onShow}
            className="flex-[1.4] rounded-xl bg-sky-500 px-4 py-3 text-sm font-semibold text-white shadow hover:bg-sky-600"
          >
            Show {productCount} product{productCount === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

function MobileSortDrawer({ open, onClose, sortBy, setSortBy }) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 md:hidden" onClick={onClose} aria-hidden />
      <div
        className="fixed inset-x-0 bottom-0 z-[61] flex max-h-[70vh] flex-col rounded-t-2xl bg-white shadow-2xl md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Sort products"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-900">Sort by</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close sort">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto py-2">
          {INVOICE_SORT_ITEMS.map((it) => {
            const active = it.key === sortBy;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => { setSortBy(it.key); onClose(); }}
                className={"w-full flex items-center justify-between px-4 py-3.5 text-sm text-left " + (active ? "bg-sky-50 text-sky-800 font-semibold" : "text-slate-800 hover:bg-slate-50")}
              >
                <span>{it.label}</span>
                {active ? <Check className="h-4 w-4 shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </>,
    document.body
  );
}

function CreditSegment({ value, onChange }) {
  const opts = ["Good", "OK", "Fair", "Poor"];
  return (
    <div>
      <div className="text-slate-900 font-semibold text-[15px] md:text-[16px] mb-2">Credit worthiness</div>
      <div className="flex flex-wrap gap-1.5">
        {opts.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold border transition-colors ${value === o ? "border-sky-400 bg-sky-100 text-sky-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >{o}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Result card ──────────────────────────────────────────────────────────────
const SHORTLIST_APPLY_LABEL = (
  <>
    Shortlist & <span className="font-bold">Apply</span>
  </>
);

function InvoiceResultCard({
  row, facilityAmount, additionalLoanAmount, showAdditionalBorrowingChip,
  shortlisted, shortlistIsFull, onToggleShortlist,
  detailsOpen, onToggleDetails, isMatchHighlight, tourAnchor = false, onTrackClick,
}) {
  const detailsAnchorRef = React.useRef(null);
  const [detailsPosition, setDetailsPosition] = React.useState(null);

  const aprPct = Number(row.ratePct);
  const advanceShown = row.netAdvance;
  const feeAmount = getMinServiceFeeAmount(row, facilityAmount ?? row?.netAdvance);
  const costTermShown = row.costOverProductTerm;
  const productId = row.id || "—";
  const contractTermLabel = Number.isFinite(row.termMonths) ? `${row.termMonths} months` : "—";
  const monthlyRepayment = row.termMonths > 0 && Number.isFinite(row.costOverProductTerm)
    ? Math.round((Number(row.costOverProductTerm) / row.termMonths) * 100) / 100
    : null;
  const ipPct = typeof row.invoicePurchasePct === "number" && Number.isFinite(row.invoicePurchasePct)
    ? `${Math.round(row.invoicePurchasePct)}%` : "—";
  const feeShownLabel = Number.isFinite(feeAmount) ? `£${Num(feeAmount)}` : "—";

  React.useLayoutEffect(() => {
    if (!detailsOpen) { setDetailsPosition(null); return; }
    const update = () => {
      const win = typeof window !== "undefined" ? window : { innerWidth: 800, innerHeight: 600 };
      // Mobile: compact bottom sheet — skip anchor positioning
      if (win.innerWidth < 768) {
        setDetailsPosition({ mobile: true });
        return;
      }
      const card = document.querySelector(`[data-invoice-product-id="${CSS.escape(String(row.id))}"]`);
      const toggles = card ? Array.from(card.querySelectorAll('[data-role="details-toggle"]')) : [];
      const el = toggles.find((t) => t.offsetParent !== null) || detailsAnchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const panelW = Math.min(640, win.innerWidth * 0.9);
      const marginRight = 24, marginLeft = 16;
      let left = r.left;
      if (left + panelW > win.innerWidth - marginRight) left = win.innerWidth - panelW - marginRight;
      if (left < marginLeft) left = marginLeft;
      const spaceBelow = win.innerHeight - (r.bottom + 8);
      const openBelow = spaceBelow >= 320;
      if (openBelow) setDetailsPosition({ top: r.bottom + 8, left, bottom: undefined, mobile: false });
      else setDetailsPosition({ top: undefined, left, bottom: win.innerHeight - r.top + 8, mobile: false });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [detailsOpen, row.id]);

  React.useEffect(() => {
    if (!detailsOpen) return;
    if (typeof window === "undefined" || window.innerWidth >= 768) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [detailsOpen]);

  const isScheduled =
    row?._isScheduled === true || isInvoiceFinanceProductScheduled(row);
  const handleToggleShortlist = (e) => {
    e.stopPropagation();
    if (isScheduled) return;
    onToggleShortlist?.();
  };
  const handleToggleDetails = (e) => { e.stopPropagation(); onToggleDetails?.(); };
  const handleTrackInteraction = React.useCallback(() => {
    if (isScheduled) return;
    Promise.resolve(onTrackClick?.(row)).catch(() => {});
  }, [isScheduled, onTrackClick, row]);
  const isMobileDetails =
    detailsPosition?.mobile === true ||
    (detailsOpen &&
      detailsPosition == null &&
      typeof window !== "undefined" &&
      window.innerWidth < 768);

  return (
    <div
      data-invoice-product-id={row.id}
      data-match-highlight={isMatchHighlight ? "true" : undefined}
      className="scroll-mt-[7.5rem]"
      onClickCapture={handleTrackInteraction}
    >
      <div className={"relative min-w-0 rounded-2xl border " + (isMatchHighlight ? "border-amber-400 bg-yellow-100 ring-2 ring-amber-400/60 shadow-md shadow-amber-200/40" : "border-slate-300 bg-white")}>
        {isScheduled && (
          <div className="absolute inset-0 z-10 pointer-events-none rounded-2xl bg-white/60 backdrop-blur-[1px]" />
        )}
        {isScheduled && !detailsOpen && (
          <div className="absolute inset-0 z-[50] pointer-events-none flex flex-col items-center justify-center gap-2">
            <div className="z-[60]">
              <span className="comingSoon-pill inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white relative">
                <span className="comingSoon-dot" />
                <span className="relative z-10">Coming soon</span>
              </span>
            </div>
            <div className="text-center px-6 z-[60]">
              <div className="text-[36px] md:text-[48px] font-extrabold tracking-tight text-sky-400 drop-shadow-sm">
                Launching shortly
              </div>
            </div>
          </div>
        )}

        {/* Mobile header: chips scroll full width; Shortlist sits clear on the right */}
        <div className={`relative flex md:hidden items-center rounded-t-2xl border-b border-slate-200 px-3 py-2 min-w-0 ${isScheduled ? "opacity-70" : ""}`}>
          <div
            className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden hide-scrollbar touch-pan-x overscroll-x-contain pr-[5.75rem]"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="flex w-max max-w-none flex-nowrap items-center gap-1 py-0.5">
              <InvoiceProductChips
                invoicePurchasePct={row.invoicePurchasePct}
                dpAllowed={row.dpAllowed}
                additionalLoanAmount={additionalLoanAmount}
                maxAbAmount={row.maxAbAmount}
                showAdditionalBorrowingChip={showAdditionalBorrowingChip}
                reviewLimitAmount={calcReviewLimit(facilityAmount)}
                compact
                nowrap
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleShortlist}
            disabled={isScheduled || (!shortlisted && shortlistIsFull)}
            {...(tourAnchor ? { id: "invoice-add-to-shortlist-mobile" } : {})}
            className={
              "absolute right-3 top-1/2 -translate-y-1/2 z-[1] shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold border bg-white transition " +
              (isScheduled
                ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-50"
                : shortlisted
                  ? "border-sky-300 bg-sky-100 text-sky-700"
                  : !shortlisted && shortlistIsFull
                    ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "border-slate-200 text-slate-700 hover:bg-slate-50")
            }
            title={
              isScheduled
                ? "Product not yet available"
                : shortlisted
                  ? "Added to shortlist"
                  : shortlistIsFull
                    ? "Shortlist full (max 5)"
                    : "Shortlist"
            }
            aria-label={shortlisted ? "Remove from shortlist" : shortlistIsFull ? "Shortlist full" : "Shortlist"}
          >
            {shortlisted ? (
              <>
                <BookmarkCheck className="h-3.5 w-3.5 shrink-0" />
                <span className="whitespace-nowrap leading-none">Added</span>
              </>
            ) : shortlistIsFull ? (
              <>
                <Bookmark className="h-3.5 w-3.5 shrink-0" />
                <span className="whitespace-nowrap leading-none">Full</span>
              </>
            ) : (
              <>
                <Bookmark className="h-3.5 w-3.5 shrink-0" />
                <span className="whitespace-nowrap leading-none">Shortlist</span>
              </>
            )}
          </button>
        </div>

        {/* Desktop header */}
        <div className={`hidden md:flex items-center justify-between gap-3 rounded-t-2xl border-b border-slate-200 px-3 py-2.5 ${isScheduled ? "opacity-70" : ""}`}>
          <div className="min-w-0 flex items-center gap-2 flex-wrap">
            <InvoiceProductChips
              invoicePurchasePct={row.invoicePurchasePct}
              dpAllowed={row.dpAllowed}
              additionalLoanAmount={additionalLoanAmount}
              maxAbAmount={row.maxAbAmount}
              showAdditionalBorrowingChip={showAdditionalBorrowingChip}
              reviewLimitAmount={calcReviewLimit(facilityAmount)}
            />
          </div>
          <div ref={detailsAnchorRef} className="flex items-center gap-2 md:gap-3 shrink-0">
            <button
              {...(tourAnchor ? { id: "invoice-add-to-shortlist" } : {})}
              type="button" onClick={handleToggleShortlist}
              disabled={isScheduled || (!shortlisted && shortlistIsFull)}
              className={
                "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition " +
                (isScheduled
                  ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-50"
                  : shortlisted
                    ? "border-sky-300 bg-sky-100 text-sky-700"
                    : shortlistIsFull
                      ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
              }
              title={
                isScheduled
                  ? "Product not yet available"
                  : shortlisted
                    ? "Added to shortlist"
                    : shortlistIsFull
                      ? "Shortlist full (max 5)"
                      : "Shortlist and apply"
              }
            >
              {shortlisted ? <><BookmarkCheck className="h-5 w-5" />Added to shortlist</> : shortlistIsFull ? <><Bookmark className="h-5 w-5" />Shortlist full</> : <><Bookmark className="h-5 w-5" />{SHORTLIST_APPLY_LABEL}</>}
            </button>
            <button type="button" data-role="details-toggle" onClick={handleToggleDetails}
              className="inline-flex items-center gap-1.5 text-[16px] md:text-[17px] font-semibold text-sky-700 hover:text-sky-800 transition-colors"
            >
              Full details
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${detailsOpen ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        {/* Mobile compact metrics */}
        <div className="flex md:hidden items-stretch gap-2 px-3 py-2">
          <div className="w-[118px] shrink-0 flex flex-col items-center justify-center rounded-xl bg-sky-500 px-2 py-2.5 text-white shadow-sm">
            <div className="text-[10px] font-semibold leading-tight text-center">APR</div>
            <SmoothPct value={aprPct} className="mt-0.5 text-xl font-extrabold leading-none" />
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5 py-0.5">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-500 shrink-0">Net Advance</span>
              <SmoothMoney value={advanceShown} className="font-bold text-slate-900 truncate" />
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-500 shrink-0">Min. fee</span>
              <SmoothMoney value={feeAmount} className="font-bold text-slate-900 truncate" />
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-slate-500 shrink-0">Cost over term</span>
              <SmoothMoney value={costTermShown} className="font-bold text-slate-900 truncate" />
            </div>
          </div>
        </div>

        {/* Mobile full details footer */}
        <div className="md:hidden border-t border-slate-200 px-3 py-2">
          <button
            type="button"
            data-role="details-toggle"
            onClick={handleToggleDetails}
            className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-800 transition-colors"
          >
            Full details
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${detailsOpen ? "rotate-180" : ""}`} />
          </button>
        </div>

        {/* Desktop metrics */}
        <div className="hidden md:block px-3 py-2.5">
          <div className="grid grid-cols-[max-content,1fr,1fr,1fr] gap-x-2 items-center">
            <div className="w-full flex flex-col items-center justify-center rounded-xl bg-sky-500 px-6 py-4 min-h-[84px] text-white shadow-sm">
              <div className="text-[17px] font-semibold leading-tight">APR</div>
              <SmoothPct value={aprPct} className="mt-0.5 text-[34px] font-extrabold leading-none" />
            </div>
            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">Net Advance</div>
              <SmoothMoney value={advanceShown} className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none" />
            </div>
            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">Min. service fee</div>
              <SmoothMoney value={feeAmount} className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none" />
            </div>
            <div className="flex flex-col items-center justify-center text-center">
              <div className="text-[17px] text-slate-600 leading-tight">Cost over product term</div>
              <SmoothMoney value={costTermShown} className="mt-0.5 text-[24px] font-extrabold text-slate-900 leading-none" />
            </div>
          </div>
        </div>

        {/* Full details — mobile bottom sheet / desktop popover */}
        {detailsOpen && createPortal(
          <>
            {isMobileDetails ? (
              <div
                className="fixed inset-0 z-[99998] bg-black/40 md:hidden"
                onClick={handleToggleDetails}
                aria-hidden
              />
            ) : null}
            <div
              data-role="details-popover"
              className={
                isMobileDetails
                  ? "fixed z-[99999] inset-x-2.5 bottom-[4.5rem] max-h-[min(78dvh,560px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl flex flex-col"
                  : "fixed z-[99999] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl flex flex-col w-[min(640px,90vw)]"
              }
              style={
                isMobileDetails
                  ? { zIndex: 99999 }
                  : {
                      zIndex: 99999,
                      top: detailsPosition?.top != null ? `${detailsPosition.top}px` : detailsPosition ? "auto" : "1.5rem",
                      left: detailsPosition ? `${detailsPosition.left}px` : "1.5rem",
                      right: detailsPosition ? "auto" : "1.5rem",
                      bottom: detailsPosition?.bottom != null ? `${detailsPosition.bottom}px` : undefined,
                    }
              }
            >
              <div className="flex-shrink-0 flex items-center justify-between gap-2 bg-white border-b border-slate-200 px-3 py-2 md:px-6 md:py-4 md:min-h-[60px] rounded-t-2xl">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] md:text-xs font-semibold uppercase tracking-wide text-sky-600">Product details</div>
                  <div className="mt-0.5 text-sm md:text-lg font-semibold text-slate-900 truncate pr-2">
                    {formatInvoiceFinanceProductTitle(row)}
                  </div>
                  <div className="mt-0.5 text-[11px] md:text-xs text-slate-600">Product ID: <span className="font-semibold text-slate-900">{productId}</span></div>
                </div>
                <button type="button" onClick={handleToggleDetails} aria-label="Close details"
                  className="flex-shrink-0 flex items-center justify-center rounded-xl bg-slate-200 text-slate-600 hover:bg-slate-300 active:bg-slate-400 font-semibold min-h-[36px] min-w-[36px] p-0 md:h-9 md:w-9 md:min-h-0 md:min-w-0 md:rounded-lg md:border md:border-slate-200 md:bg-transparent md:text-slate-700 md:hover:bg-slate-50"
                ><X className="h-5 w-5" /></button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-2 pb-2.5 md:px-5 md:py-3 md:pb-4">
                <div className={`grid gap-2.5 md:gap-5 ${showAdditionalBorrowingChip ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
                  <div className="min-w-0">
                    <dl className="grid grid-cols-2 md:grid-cols-1 gap-x-2 gap-y-1 md:gap-y-1.5 [&>*]:flex [&>*]:flex-col [&>*]:items-start [&>*]:gap-0 [&>*]:min-w-0 [&_dt]:text-[10px] md:[&_dt]:text-sm [&_dd]:text-left [&_dd]:text-[11px] md:[&_dd]:text-sm md:[&>*]:flex-row md:[&>*]:items-center md:[&>*]:justify-between md:[&_dd]:text-right">
                      <DetailRow label="APR" value={Number.isFinite(aprPct) ? `${aprPct.toFixed(2)}%` : "—"} strong />
                      <DetailRow label="Net Advance" value={`£${Num(row.netAdvance)}`} strong />
                      <DetailRow label="Min. service fee" value={feeShownLabel} strong />
                      <DetailRow label="Contract term" value={contractTermLabel} />
                      <DetailRow
                        label={
                          <>
                            <span className="md:hidden">IP</span>
                            <span className="hidden md:inline">Initial percentage (IP)</span>
                          </>
                        }
                        value={ipPct}
                        strong
                      />
                      <DetailRow
                        label="Review limit"
                        value={`£${Num(calcReviewLimit(facilityAmount))}`}
                        strong
                      />
                      <DetailRow label="Headline facility" value={`£${Num(row.headlineAmount)}`} strong />
                      <DetailRow label="Service fee" value={`${formatPercentRaw(row.annualCostPct)}%`} />
                      <DetailRow label="Facility utilisation" value={`${Number(row.utilisationPct ?? 0)}%`} />
                      <DetailRow label="Utilised facility" value={`£${Num(row.utilisedFacility)}`} />
                      <DetailRow label="Discount fee cost" value={`£${Num(row.discountFeeAmount)}`} />
                      <DetailRow label="Service fee cost" value={`£${Num(row.serviceFeeAmount)}`} />
                      <DetailRow label="Arrangement fee cost" value={`£${Num(row.arrangementFeeAmount)}`} />
                      <DetailRow label="Other fixed fees" value={`£${Num(row.fixedFeeAmount)}`} />
                    </dl>
                  </div>
                  {showAdditionalBorrowingChip ? (
                    row.abCalc ? (
                      <div className="min-w-0 md:border-l md:border-slate-200 md:pl-5 pt-1.5 md:pt-0 border-t border-slate-100 md:border-t-0">
                        <div className="text-[11px] md:text-xs font-semibold text-slate-900 mb-1 md:mb-1.5 inline-flex items-center gap-1.5 flex-wrap">
                          Additional Borrowing
                          <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[9px] md:text-[10px] font-bold uppercase tracking-wide text-sky-700 leading-none">
                            Capital &amp; Interest
                          </span>
                        </div>
                        <dl className="grid grid-cols-2 md:grid-cols-1 gap-x-2 gap-y-1 md:gap-y-1.5 [&>*]:flex [&>*]:flex-col [&>*]:items-start [&>*]:gap-0 [&>*]:min-w-0 [&_dt]:text-[10px] md:[&_dt]:text-sm [&_dd]:text-left [&_dd]:text-[11px] md:[&_dd]:text-sm md:[&>*]:flex-row md:[&>*]:items-center md:[&>*]:justify-between md:[&_dd]:text-right">
                          <DetailRow label="Loan amount" value={`£${Num(row.abCalc.abPrincipal)}`} strong />
                          <DetailRow label="Interest rate" value={`${row.abCalc.interestRatePct}%`} />
                          <DetailRow label="First 12 months interest" value={`£${Num(row.abCalc.first12MonthsInterest)}`} />
                          <DetailRow label="Arrangement fee" value={`£${Num(row.abCalc.arrangementFee)}`} />
                          <DetailRow label="Contract term" value={`${row.abCalc.termMonths} months`} />
                          <DetailRow label="Monthly repayment" value={`£${Num(row.abCalc.monthlyRepayment)}`} />
                          <DetailRow label="Total repayable" value={`£${Num(row.abCalc.totalRepayable)}`} strong />
                          <DetailRow label="Cost over product term" value={`£${Num(row.abCalc.costOverTerm)}`} strong />
                        </dl>
                      </div>
                    ) : (
                      <div className="min-w-0 md:border-l md:border-slate-200 md:pl-5 pt-1.5 md:pt-0 border-t border-slate-100 md:border-t-0">
                        <div className="text-[11px] md:text-xs font-semibold text-slate-900 mb-1 md:mb-1.5">Additional Borrowing</div>
                        <p className="text-xs text-slate-500">
                          Not available for this product. Additional borrowing is only shown for Capital Repayment products.
                        </p>
                      </div>
                    )
                  ) : null}
                </div>
                <p className="mt-2 md:mt-3 text-[10px] md:text-[11px] text-slate-500 leading-snug">
                  {showAdditionalBorrowingChip
                    ? "Cost over product term includes discount fee, service fee, arrangement fee, renewal fee, fixed fees and VAT where applicable, plus the first 12 months interest on additional borrowing where shown. Facility principal is not included."
                    : "Cost over product term is discount fee, service fee, arrangement fee, renewal fee, fixed fees and VAT where applicable. Facility principal is not included."}
                </p>
              </div>
            </div>
          </>,
          document.body
        )}
      </div>
    </div>
  );
}

// ─── Guided tour ──────────────────────────────────────────────────────────────
function InvoiceLockedResultsGate({ hiddenRows, hiddenCount, totalCount, onUnlock, facilityAmount }) {
  // Show a couple of real (blurred) cards behind the CTA so the user can see there's more.
  const teasers = hiddenRows.slice(0, 2);
  return (
    <div className="relative mt-2">
      <div className="pointer-events-none select-none blur-[6px] opacity-70" aria-hidden="true">
        {teasers.map((row) => (
          <div key={row.id} className="mb-2.5">
            <InvoiceResultCard
              row={row}
              facilityAmount={facilityAmount}
              shortlisted={false}
              shortlistIsFull={false}
              onToggleShortlist={() => {}}
              detailsOpen={false}
              onToggleDetails={() => {}}
              isMatchHighlight={false}
            />
          </div>
        ))}
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-start rounded-2xl bg-gradient-to-b from-white/40 via-white/80 to-white pt-4 sm:pt-6">
        <div className="w-full flex flex-col items-center text-center px-4 pb-4 pt-2">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h3 className="text-base font-bold text-slate-900">
            {hiddenCount} more product{hiddenCount === 1 ? "" : "s"} match your search
          </h3>
          <p className="mt-1 max-w-sm text-sm text-slate-600">
            Register for free to unlock the full list and keep sourcing.
          </p>
          <button
            type="button"
            onClick={onUnlock}
            className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-xl bg-sky-500 px-6 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-sky-600"
          >
            See all {totalCount} products — free
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoiceOnboarding({ open, onStartTour, onSkip }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[950] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" aria-hidden="true" />
      <div className="relative z-10 w-[min(480px,100%)] rounded-3xl bg-white shadow-2xl border border-sky-100 p-6 md:p-7">
        <div className="flex items-start">
          <div className="min-w-0 pr-8">
            <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-1.5">
              Get started with Invoice Finance sourcing
            </h2>
            <p className="text-sm text-slate-600 mb-3">
              A quick, 5-step tour of the key controls so you can find the right facility with confidence.
            </p>
            <ul className="space-y-1.5 text-sm text-slate-700 mb-4">
              <li>
                <span className="font-semibold text-slate-900">Quick bar at the top</span>{" "}
                – choose your <span className="font-medium">sector</span> and enter{" "}
                <span className="font-medium">projected turnover</span>; facility is calculated automatically.
              </li>
              <li>
                <span className="font-semibold text-slate-900">Left-hand filters</span>{" "}
                – narrow by <span className="font-medium">credit terms, contract term, ledger type</span> and more.
              </li>
              <li>
                <span className="font-semibold text-slate-900">Results panel</span>{" "}
                – compare <span className="font-medium">APR, net advance, fees</span> and cost before shortlisting.
              </li>
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={onStartTour}
                className="inline-flex items-center justify-center rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1"
              >
                Start guided tour
              </button>
              <button
                type="button"
                onClick={onSkip}
                className="text-xs text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
              >
                I&apos;ll explore on my own
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={onSkip}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"
            aria-label="Close onboarding"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const INVOICE_TOUR_STEPS = [
  {
    id: "invoice-quickbar",
    title: "Step 1 – Set your case numbers",
    body:
      "Use the blue quick bar to pick your sector and enter projected turnover. Facility required auto-fills, " +
      "and these drive every calculation. You can also add optional additional borrowing.",
  },
  {
    id: "invoice-results",
    title: "Step 2 – Browse matched facilities",
    body:
      "Matching products appear here instantly. Compare APR, net advance, minimum service fee and total cost " +
      "over the product term at a glance.",
  },
  {
    id: "invoice-filters",
    mobileId: "invoice-mobile-filters-trigger",
    title: "Step 3 – Narrow with smart filters",
    body:
      "Use the filters to refine by client type, credit terms, contract term, ledger type, concentration and more. " +
      "Results update automatically. On mobile, tap Filters to open the panel.",
  },
  {
    id: "invoice-add-to-shortlist",
    mobileId: "invoice-add-to-shortlist-mobile",
    title: "Step 4 – Shortlist your favourites",
    body:
      "Found a facility you like? Tap \"Add to shortlist\" on any product card. You can shortlist up to 5 and " +
      "compare them side-by-side.",
  },
  {
    id: "invoice-shortlist-cart-window",
    title: "Step 5 – Review your shortlist",
    body:
      "Open your shortlist cart to compare shortlisted facilities, then continue to apply with your shortlisted products.",
  },
];

function InvoiceTour({ open, stepIndex, onNext, onPrev, onClose, onEnsureStep, canNext, nextHint }) {
  const [targetRect, setTargetRect] = React.useState(null);
  const [windowSize, setWindowSize] = React.useState({ width: 0, height: 0 });
  const [scrollSyncing, setScrollSyncing] = React.useState(false);
  const scrollIdleTimerRef = React.useRef(null);
  const scrollRafRef = React.useRef(0);

  const steps = INVOICE_TOUR_STEPS;
  const step = steps[stepIndex] ?? steps[0];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const resolveTargetId = React.useCallback(
    (s) => (typeof window !== "undefined" && window.innerWidth < 768 && s.mobileId ? s.mobileId : s.id),
    []
  );

  const updateRect = React.useCallback(() => {
    if (!open) { setTargetRect(null); return; }
    const el = document.getElementById(resolveTargetId(step));
    if (!el) { setTargetRect(null); return; }
    const rect = el.getBoundingClientRect();
    setTargetRect({
      top: rect.top - 8,
      left: rect.left - 8,
      width: rect.width + 16,
      height: rect.height + 16,
      bottom: rect.bottom + 8,
      right: rect.right + 8,
    });
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });
  }, [open, step, resolveTargetId]);

  const handleScrollOrResize = React.useCallback(() => {
    setScrollSyncing(true);
    if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
    scrollIdleTimerRef.current = setTimeout(() => setScrollSyncing(false), 140);
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0;
      updateRect();
    });
  }, [updateRect]);

  React.useEffect(() => {
    if (!open) {
      setTargetRect(null);
      setScrollSyncing(false);
      return undefined;
    }
    onEnsureStep?.(step.id);
    const el = document.getElementById(resolveTargetId(step));
    if (el) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
    }
    const measure = () => { updateRect(); requestAnimationFrame(updateRect); };
    const timer1 = setTimeout(measure, 80);
    const timer2 = setTimeout(measure, 360);
    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
    };
  }, [open, step, resolveTargetId, updateRect, handleScrollOrResize, onEnsureStep]);

  if (!open) return null;

  const bubbleStyles = {};
  if (targetRect) {
    const spaceBelow = windowSize.height - targetRect.bottom;
    const spaceAbove = targetRect.top;
    const placeAbove = spaceBelow < 250 && spaceAbove > spaceBelow;
    const bubbleWidth = windowSize.width < 768
      ? Math.min(340, Math.max(280, windowSize.width - 16))
      : 360;
    const padding = windowSize.width < 768 ? 8 : 20;
    let left = targetRect.left + targetRect.width / 2 - bubbleWidth / 2;
    left = Math.max(padding, Math.min(left, windowSize.width - bubbleWidth - padding));
    bubbleStyles.left = left;

    if (placeAbove) {
      bubbleStyles.top = Math.max(padding, targetRect.top - 20);
      bubbleStyles.transform = "translateY(-100%)";
    } else {
      bubbleStyles.top = Math.min(windowSize.height - 200, targetRect.bottom + 20);
    }

    // Desktop-only side placements for tall panels.
    if (windowSize.width >= 768 && step.id === "invoice-results") {
      bubbleStyles.top = "50%";
      bubbleStyles.transform = "translateY(-50%)";
      const desiredLeft = targetRect.left - bubbleWidth - 24;
      bubbleStyles.left = desiredLeft > padding ? desiredLeft : padding;
    } else if (windowSize.width >= 768 && step.id === "invoice-filters") {
      bubbleStyles.top = "50%";
      bubbleStyles.transform = "translateY(-50%)";
      const desiredLeft = targetRect.right + 24;
      bubbleStyles.left = desiredLeft + bubbleWidth > windowSize.width - padding
        ? windowSize.width - bubbleWidth - padding
        : desiredLeft;
    }
  }

  const maskX = targetRect ? targetRect.left : 0;
  const maskY = targetRect ? targetRect.top : 0;
  const maskW = targetRect ? targetRect.width : 0;
  const maskH = targetRect ? targetRect.height : 0;

  return (
    <div className="fixed inset-0 z-[9990] pointer-events-none flex items-center justify-center">
      <svg className="fixed inset-0 pointer-events-none w-full h-full" style={{ zIndex: 9991 }} width="100%" height="100%">
        <defs>
          <mask id="invoice-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect x={maskX} y={maskY} width={maskW} height={maskH} rx="12" ry="12" fill="black" />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(15, 23, 42, 0.7)" mask="url(#invoice-spotlight-mask)" />
      </svg>

      <div className="pointer-events-none absolute inset-0 z-[9992]">
        <div
          className={
            "pointer-events-auto w-[min(340px,calc(100%-16px))] rounded-3xl bg-white shadow-2xl border-4 border-sky-100 p-3 md:p-2.5 max-h-[360px] md:max-h-[300px] overflow-hidden flex flex-col " +
            (targetRect ? "absolute" : "relative mx-auto mt-[25vh]") +
            (scrollSyncing ? "" : " transition-[top,left,transform] duration-200 ease-out")
          }
          style={targetRect ? bubbleStyles : undefined}
        >
          <div className="flex flex-col gap-1 flex-1 min-h-0 pb-1">
            <img
              src="/Waving-Photoroom.png"
              alt="Tour guide"
              className="mx-auto mb-2 w-[106px] h-[86px] md:w-[100px] md:h-[82px] object-contain select-none pointer-events-none"
              draggable={false}
            />
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-[12px] md:text-[13px] font-bold text-slate-900 leading-tight pr-2">{step.title}</h2>
                <p className="text-[9px] md:text-[10px] font-semibold uppercase tracking-wider text-sky-500 mt-1">
                  Step {stepIndex + 1} of {steps.length}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex shrink-0 h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors -mt-1 -mr-1"
                aria-label="Close tour"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex gap-2.5 mt-2.5 items-start">
              <p className="text-[11px] text-slate-700 leading-relaxed font-medium max-h-[120px] md:max-h-[72px] overflow-auto">
                {step.body}
              </p>
            </div>
            {!canNext && nextHint ? (
              <p className="mt-1 mb-0 text-[10px] leading-tight font-semibold text-sky-600">{nextHint}</p>
            ) : null}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="text-[13px] font-semibold text-slate-400 hover:text-slate-600 underline-offset-4 hover:underline transition-colors"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2 md:gap-3">
              <button
                type="button"
                onClick={onPrev}
                disabled={isFirst}
                className={
                  "inline-flex items-center justify-center rounded-xl border px-3 py-1.5 text-[12px] font-semibold transition-colors " +
                  (isFirst
                    ? "border-slate-100 bg-slate-50 text-slate-300 cursor-default"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm")
                }
              >
                Back
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={!canNext}
                className="inline-flex items-center justify-center rounded-xl bg-sky-500 px-3 py-1.5 text-[12px] font-bold text-white shadow-md hover:bg-sky-600 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLast ? "Done" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function InvoiceFinanceSourcing() {
  const { track: trackEngagementEvent } = useSourcingEngagement("IF");
  const navigate = useNavigate();

  const trackClickForProduct = React.useCallback(async (row) => {
    const productId = row?._raw?.name || row?.id;
    if (!productId) return;
    try {
      await trackInvoiceFinanceClick({ product_name: productId });
      trackEngagementEvent("product_view", { product: productId });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Click tracking failed:", err?.message || err);
    }
  }, [trackEngagementEvent]);
  const {
    open: reviewPromptOpen,
    dismiss: dismissReviewPrompt,
    openNow: openReviewPrompt,
  } = useReviewPrompt({ storageKey: "if_sourcing", delayMs: REVIEW_PROMPT_DELAY_MS, requireInteraction: true });

  React.useEffect(() => {
    rememberSourcingCategory("IF");
  }, []);

  const [storedQs] = React.useState(() => readStoredIfSourcingQs());
  const [applicationMode, setApplicationMode] = React.useState(
    () => storedQs.applicationMode || "new"
  );
  const [sector, setSector] = React.useState(() => String(storedQs.sector || ""));

  // Turnover drives auto-calc; facility is editable but pre-filled from turnover
  const [projectedTurnover, setProjectedTurnover] = React.useState(
    () => String(storedQs.projectedTurnover || "")
  );
  const [facilityRequired, setFacilityRequired] = React.useState(
    () => String(storedQs.facilityRequired || "")
  );
  const [additionalBorrowingType, setAdditionalBorrowingType] = React.useState("amount");
  const [additionalBorrowingValue, setAdditionalBorrowingValue] = React.useState(
    () => String(storedQs.additionalBorrowingValue || "")
  );
  const [facilityUtilisationPct, setFacilityUtilisationPct] = React.useState(() => {
    const n = Number(storedQs.facilityUtilisationPct);
    return Number.isFinite(n) ? n : 60;
  });

  const [existingIp, setExistingIp] = React.useState("");
  const [existingDiscount, setExistingDiscount] = React.useState("");
  const [existingServiceFee, setExistingServiceFee] = React.useState("");
  const [existingMiniServiceFee, setExistingMiniServiceFee] = React.useState("");
  const [showExistingFloat, setShowExistingFloat] = React.useState(
    () => storedQs.showExistingFloat !== false
  );

  const [filters, setFilters] = React.useState(() => readStoredIfSourcingFilters());
  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));

  // Persist quick-search inputs (and related fields) across reload — same pattern as BTL.
  React.useEffect(() => {
    try {
      localStorage.setItem(
        IF_SOURCING_QS_KEY,
        JSON.stringify({
          applicationMode,
          sector,
          projectedTurnover,
          facilityRequired,
          additionalBorrowingType: "amount",
          additionalBorrowingValue,
          facilityUtilisationPct,
          showExistingFloat,
        })
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, [
    applicationMode,
    sector,
    projectedTurnover,
    facilityRequired,
    additionalBorrowingValue,
    facilityUtilisationPct,
    showExistingFloat,
  ]);

  React.useEffect(() => {
    try {
      localStorage.setItem(IF_SOURCING_FILTERS_KEY, JSON.stringify(filters));
    } catch {
      /* ignore */
    }
  }, [filters]);

  const [sectorOptions] = React.useState([
    "",
    ...filterInvoiceFinanceSectorOptions(INVOICE_FINANCE_SECTORS),
  ]);
  const [results, setResults] = React.useState([]);
  const [catalogueCount, setCatalogueCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [sourcingError, setSourcingError] = React.useState("");
  const [calcSeconds, setCalcSeconds] = React.useState("0.000");
  const [sortBy, setSortBy] = React.useState("rate");
  const [invoiceShortlist, setInvoiceShortlist] = React.useState(() => new Set());
  const [shortlistedInvoiceMap, setShortlistedInvoiceMap] = React.useState(() => new Map());
  const shortlistedInvoiceMapRef = React.useRef(shortlistedInvoiceMap);
  shortlistedInvoiceMapRef.current = shortlistedInvoiceMap;
  const [cartOpen, setCartOpen] = React.useState(false);
  const [showCartMascot, setShowCartMascot] = React.useState(false);
  const cartMascotSuppressRef = React.useRef(false);
  const [shortlistViewMode, setShortlistViewMode] = React.useState("list");
  const [compareHighlight, setCompareHighlight] = React.useState(false);
  const [openInvoiceDetailsId, setOpenInvoiceDetailsId] = React.useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);
  const [mobileSortOpen, setMobileSortOpen] = React.useState(false);

  // ── Guided tour ──────────────────────────────────────────────────────────────
  const [showOnboarding, setShowOnboarding] = React.useState(() => {
    if (typeof window === "undefined" || window.innerWidth < 768) return false;
    try { return !localStorage.getItem("if_polar_onboarding_seen"); } catch { return true; }
  });
  const [tourOpen, setTourOpen] = React.useState(false);
  const [tourStepIndex, setTourStepIndex] = React.useState(0);
  const tourStepId = INVOICE_TOUR_STEPS[tourStepIndex]?.id || "invoice-quickbar";
  const tourFiltersBaselineRef = React.useRef(null);

  const [showSignupPrompt, setShowSignupPrompt] = React.useState(false);
  const [signupPromptStartOnForm, setSignupPromptStartOnForm] = React.useState(false);

  // Soft signup prompt for guests after 40s. Closing (X / Not now) dismisses for this session.
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isAuthenticated()) return undefined;
    try {
      if (sessionStorage.getItem(IF_SIGNUP_PROMPT_KEY) === "1") return undefined;
    } catch {
      /* ignore */
    }

    const timer = window.setTimeout(() => {
      if (isAuthenticated()) return;
      try {
        if (sessionStorage.getItem(IF_SIGNUP_PROMPT_KEY) === "1") return;
      } catch {
        /* ignore */
      }
      setSignupPromptStartOnForm(false);
      setShowSignupPrompt(true);
    }, 40000);

    return () => window.clearTimeout(timer);
  }, []);

  const dismissSignupPrompt = React.useCallback(() => {
    setShowSignupPrompt(false);
    setSignupPromptStartOnForm(false);
    try {
      sessionStorage.setItem(IF_SIGNUP_PROMPT_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const handleSignupPromptSuccess = React.useCallback(({ keepOpen = false } = {}) => {
    if (!keepOpen) {
      setShowSignupPrompt(false);
      setSignupPromptStartOnForm(false);
    }
    try {
      sessionStorage.setItem(IF_SIGNUP_PROMPT_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const autoScrollKeyRef = React.useRef("");
  const autoScrolledKeysRef = React.useRef(new Set());
  /** Cancel fn for an in-flight bumpy scroll (user scrolled up). */
  const cancelBumpyScrollRef = React.useRef(null);
  const cartButtonRef = React.useRef(null);
  const cartPopoverRef = React.useRef(null);

  const shortlistIsFull = invoiceShortlist.size >= 5;

  // ── Input helpers ────────────────────────────────────────────────────────────
  const parseNumericInput = React.useCallback((value) => {
    if (value == null) return NaN;
    const cleaned = String(value).replace(/,/g, "").trim();
    if (!cleaned) return NaN;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  }, []);

  const formatWithCommas = React.useCallback((value) => {
    const [intPart, decPart] = String(value).split(".");
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decPart != null ? `${withCommas}.${decPart}` : withCommas;
  }, []);

  const normalizeNumberInput = React.useCallback((raw, { allowDecimal = true } = {}) => {
    const cleaned = String(raw || "").replace(/,/g, "");
    const pattern = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
    if (!pattern.test(cleaned)) return null;
    if (!cleaned) return "";
    if (cleaned.endsWith(".")) return formatWithCommas(cleaned.slice(0, -1)) + ".";
    const normalized = cleaned.replace(/^0+(?=\d)/, "");
    return formatWithCommas(normalized);
  }, [formatWithCommas]);

  // ── Derived values ───────────────────────────────────────────────────────────
  const turnoverNum = parseNumericInput(projectedTurnover) || 0;
  const facilityNum = parseNumericInput(facilityRequired) || 0;

  const { invoiceValue, facilityNum: autoFacilityNum } = React.useMemo(
    () => calcFacilityFromTurnover(turnoverNum, filters.creditTermsDays),
    [turnoverNum, filters.creditTermsDays]
  );

  // Auto-fill facility when turnover or credit terms change.
  // Manual facility edits stick until turnover/credit terms change again.
  // On reload, keep a restored facility value (don't overwrite once).
  const facilityTouchedRef = React.useRef(false);
  const autoFillKeyRef = React.useRef("");
  const restoredFacilityValueRef = React.useRef(String(storedQs.facilityRequired || ""));
  const restoreFacilityOnceRef = React.useRef(
    Boolean(String(storedQs.facilityRequired || "").trim())
  );
  React.useEffect(() => {
    if (restoreFacilityOnceRef.current) {
      restoreFacilityOnceRef.current = false;
      if (turnoverNum > 0) {
        const key = `${turnoverNum}|${filters.creditTermsDays}|${autoFacilityNum}`;
        autoFillKeyRef.current = key;
        const restoredNum = parseNumericInput(restoredFacilityValueRef.current) || 0;
        if (restoredNum > 0 && restoredNum !== autoFacilityNum) {
          facilityTouchedRef.current = true;
        }
      }
      return;
    }
    if (turnoverNum <= 0) {
      facilityTouchedRef.current = false;
      autoFillKeyRef.current = "";
      setFacilityRequired("");
      return;
    }
    const key = `${turnoverNum}|${filters.creditTermsDays}|${autoFacilityNum}`;
    if (facilityTouchedRef.current && autoFillKeyRef.current === key) return;
    autoFillKeyRef.current = key;
    facilityTouchedRef.current = false;
    setFacilityRequired(formatWithCommas(String(autoFacilityNum)));
  }, [turnoverNum, filters.creditTermsDays, autoFacilityNum, formatWithCommas]);

  const additionalBorrowingNum = parseNumericInput(additionalBorrowingValue);

  // ── Validation ───────────────────────────────────────────────────────────────
  const projectedTurnoverError =
    projectedTurnover !== "" && (!Number.isFinite(turnoverNum) || turnoverNum <= 0)
      ? "Projected turnover must be a positive number."
      : "";
  const facilityRequiredError =
    facilityRequired !== "" && (!Number.isFinite(facilityNum) || facilityNum <= 0)
      ? "Facility required must be a positive number."
      : "";
  const additionalBorrowingError =
    additionalBorrowingValue !== "" &&
    (!Number.isFinite(additionalBorrowingNum) || additionalBorrowingNum < 0)
      ? "Additional borrowing must be 0 or higher."
      : "";

  const [mobileValidationDialog, setMobileValidationDialog] = React.useState(null);
  const showMobileQuickValidationIfNeeded = React.useCallback((fieldLabel, message) => {
    if (!message || !isMobileViewport()) return;
    setMobileValidationDialog({ fieldLabel, message });
  }, []);

  const inputsReady =
    sector !== "" &&
    projectedTurnover !== "" &&
    !projectedTurnoverError &&
    turnoverNum > 0 &&
    facilityRequired !== "" &&
    !facilityRequiredError &&
    facilityNum > 0 &&
    !additionalBorrowingError;

  const existingInputsFilled =
    applicationMode === "existing" &&
    [existingIp, existingDiscount, existingServiceFee, existingMiniServiceFee].every(
      (s) => s !== "" && Number.isFinite(parseNumericInput(s)) && parseNumericInput(s) >= 0
    );

  const existingComparisonReady = inputsReady && existingInputsFilled;

  const existingComparisonRow = React.useMemo(() => {
    if (!existingComparisonReady) return null;
    return buildExistingComparisonRow({
      facilityAmount: facilityNum,
      ipPct: parseNumericInput(existingIp),
      discountPct: parseNumericInput(existingDiscount),
      serviceFeePct: parseNumericInput(existingServiceFee),
      miniServiceFeeAmount: parseNumericInput(existingMiniServiceFee),
    });
  }, [
    existingComparisonReady,
    facilityNum,
    existingIp,
    existingDiscount,
    existingServiceFee,
    existingMiniServiceFee,
    parseNumericInput,
  ]);

  const [shortlistBusyId, setShortlistBusyId] = React.useState(null);
  const [shortlistNotice, setShortlistNotice] = React.useState("");

  React.useEffect(() => {
    if (!shortlistNotice) return undefined;
    const t = window.setTimeout(() => setShortlistNotice(""), 4000);
    return () => window.clearTimeout(t);
  }, [shortlistNotice]);

  const hydrateIfShortlistFromBackend = React.useCallback(async () => {
    if (!isAuthenticated() || !getBorrowerEmail()) return;
    try {
      const res = await getShortlistedProducts({ limit: 20 });
      const list = Array.isArray(res?.data) ? res.data : [];
      const ifItems = list.filter((p) => {
        const cat = String(p?.product_category || p?.category || "").toUpperCase();
        return cat === "IF" || cat.includes("INVOICE");
      });

      const prevMap = shortlistedInvoiceMapRef.current || new Map();
      const nextSet = new Set();
      const nextMap = new Map();

      ifItems.forEach((p) => {
        const id = String(p.mortgage_product || p.product_id || p.id || "").trim();
        if (!id) return;
        const existing = prevMap.get(id);
          const termMonths =
            resolveInvoiceFinanceContractTermMonths({
              contract_term_months: p.contract_term_months,
              termMonths: p.termMonths,
              term_months: p.term_months,
              _raw: p,
            }) ??
            resolveInvoiceFinanceContractTermMonths(existing) ??
            null;
        nextSet.add(id);
        const cartItem = {
          ...(existing && String(existing.id) === id ? existing : {}),
          id,
          lenderName: p.lender_name || p.lenderName || existing?.lenderName || id,
          sector: p.sector ?? existing?.sector,
          invoicePurchasePct: p.ip_percent ?? p.invoicePurchasePct ?? existing?.invoicePurchasePct,
          ratePct: p.apr ?? p.ratePct ?? p.discount_rate_percent ?? existing?.ratePct,
          netAdvance: p.net_advance ?? p.netAdvance ?? existing?.netAdvance,
          costOverProductTerm:
            p.cost_over_product_term ?? p.total_cost ?? existing?.costOverProductTerm,
          facility_required: p.facility_required ?? existing?.facility_required,
          projected_turnover: p.projected_turnover ?? existing?.projected_turnover,
          additional_borrowing_amount:
            p.additional_borrowing_amount ?? existing?.additional_borrowing_amount,
          termMonths,
          term_months: termMonths,
          contract_term_months: termMonths,
          mortgage_term: p.mortgage_term ?? existing?.mortgage_term,
          facility_type: p.facility_type ?? existing?.facility_type,
          confidential_facility: p.confidential_facility ?? existing?.confidential_facility,
          confidential: p.confidential ?? existing?.confidential,
          disclosure: p.disclosure ?? existing?.disclosure,
          _raw: p,
          category: "IF",
          product_category: "IF",
        };
        cartItem.displayTitle = formatInvoiceFinanceProductTitle(cartItem);
        cartItem.name = cartItem.displayTitle;
        cartItem.product_name = cartItem.displayTitle;
        nextMap.set(id, cartItem);
      });

      setInvoiceShortlist(nextSet);
      setShortlistedInvoiceMap(nextMap);
      shortlistedInvoiceMapRef.current = nextMap;
    } catch (err) {
      console.warn("[IF Sourcing] Failed to hydrate shortlist:", err);
    }
  }, []);

  React.useEffect(() => {
    hydrateIfShortlistFromBackend();
  }, [hydrateIfShortlistFromBackend]);

  // ── Shortlist (same API/flow as BTL) ─────────────────────────────────────────
  const toggleInvoiceShortlist = React.useCallback(async (productId, row) => {
    if (row && isInvoiceFinanceProductScheduled(row)) return;
    const id = String(productId || row?.id || "").trim();
    if (!id) return;

    const isAdding = !invoiceShortlist.has(id);
    if (!isAdding) {
      if (isAuthenticated() && getBorrowerEmail()) {
        setShortlistBusyId(id);
        try {
          const result = await removeShortlistedProductByMortgageProduct(id);
          if (!result.success) {
            setShortlistNotice(result.error || "Failed to remove from shortlist");
            return;
          }
        } catch (err) {
          setShortlistNotice(err?.message || "Failed to remove from shortlist");
          return;
        } finally {
          setShortlistBusyId(null);
        }
      } else {
        try {
          const pending = JSON.parse(localStorage.getItem(STORAGE_KEYS.SHORTLIST_PENDING) || "[]");
          const next = (Array.isArray(pending) ? pending : []).filter((p) => String(p?.id) !== id);
          localStorage.setItem(STORAGE_KEYS.SHORTLIST_PENDING, JSON.stringify(next));
        } catch {
          /* ignore */
        }
      }
      setInvoiceShortlist((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setShortlistedInvoiceMap((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      return;
    }

    if (invoiceShortlist.size >= 5) {
      setShortlistNotice("Your shortlist is limited to 5 products.");
      return;
    }
    if (!row) {
      setShortlistNotice("Product not found.");
      return;
    }

    const productData = buildInvoiceFinanceShortlistProductData(row, {
      sector,
      projectedTurnover: turnoverNum,
      facilityRequired: facilityNum,
      additionalBorrowingNum: Number.isFinite(additionalBorrowingNum) ? additionalBorrowingNum : 0,
      additionalBorrowingType,
      filters,
    });

    const canUseBackendShortlist = isAuthenticated() && getBorrowerEmail();
    if (canUseBackendShortlist) {
      setShortlistBusyId(id);
      try {
        if (process.env.NODE_ENV === "development") {
          console.log("📌 [IF toggleShortlist] payload:", {
            sector: productData.sector,
            projected_turnover: productData.projected_turnover,
            facility_required: productData.facility_required,
            facility_type: productData.facility_type,
            net_advance: productData.net_advance,
            additional_borrowing_amount: productData.additional_borrowing_amount,
            ip_percent: productData.ip_percent,
            discount_rate_percent: productData.discount_rate_percent,
            simple_service_fee: productData.simple_service_fee,
            min_service_fee: productData.min_service_fee,
            apr: productData.apr,
            arrangement_fee: productData.arrangement_fee,
            confidential_facility: productData.confidential_facility,
          });
        }
        const result = await addShortlistedProduct(productData);
        if (!result.success) {
          setShortlistNotice(result.error || "Failed to add to shortlist");
          return;
        }
        setInvoiceShortlist((prev) => new Set(prev).add(id));
        setShortlistedInvoiceMap((prev) => {
          const displayTitle =
            productData.name ||
            formatInvoiceFinanceProductTitle({
              ...productData,
              ...row,
              contract_term_months:
                productData.contract_term_months ?? row.termMonths ?? row.contract_term_months,
            });
          const next = new Map(prev).set(id, {
            ...row,
            ...productData,
            id,
            termMonths:
              productData.contract_term_months ??
              productData.termMonths ??
              row.termMonths ??
              null,
            contract_term_months:
              productData.contract_term_months ??
              productData.termMonths ??
              row.termMonths ??
              null,
            displayTitle,
            name: displayTitle,
            product_name: displayTitle,
          });
          shortlistedInvoiceMapRef.current = next;
          return next;
        });
        await hydrateIfShortlistFromBackend();
      } catch (err) {
        setShortlistNotice(err?.message || "Failed to add to shortlist");
      } finally {
        setShortlistBusyId(null);
      }
      return;
    }

    // Guest: store in same pending shortlist key used by BTL (synced on login)
    try {
      localStorage.setItem(STORAGE_KEYS.POST_AUTH_REDIRECT, shortlistPathForCategory("IF"));
      const pending = JSON.parse(localStorage.getItem(STORAGE_KEYS.SHORTLIST_PENDING) || "[]");
      const list = Array.isArray(pending) ? pending : [];
      const updated = [...list.filter((p) => String(p?.id) !== id), productData].slice(0, 5);
      localStorage.setItem(STORAGE_KEYS.SHORTLIST_PENDING, JSON.stringify(updated));
    } catch {
      /* ignore */
    }
    setInvoiceShortlist((prev) => new Set(prev).add(id));
    const guestTitle =
      productData.name ||
      formatInvoiceFinanceProductTitle({
        ...productData,
        ...row,
        contract_term_months:
          productData.contract_term_months ?? row.termMonths ?? row.contract_term_months,
      });
    setShortlistedInvoiceMap((prev) =>
      new Map(prev).set(id, {
        ...row,
        ...productData,
        id,
        displayTitle: guestTitle,
        name: guestTitle,
        product_name: guestTitle,
      })
    );
  }, [
    invoiceShortlist,
    sector,
    turnoverNum,
    facilityNum,
    additionalBorrowingNum,
    additionalBorrowingType,
    filters,
    hydrateIfShortlistFromBackend,
  ]);

  const toggleInvoiceDetails = React.useCallback((productId) => {
    setOpenInvoiceDetailsId((cur) => (cur === productId ? null : productId));
  }, []);

  // ── Close popovers on outside click ─────────────────────────────────────────
  React.useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (
        t.closest?.('[data-role="details-popover"]') ||
        t.closest?.('[data-role="details-toggle"]') ||
        t.closest?.('[data-role="existing-facility-float"]') ||
        t.closest?.("#invoice-mobile-shortlist-bar") ||
        cartButtonRef.current?.contains(t) ||
        cartPopoverRef.current?.contains(t)
      ) return;
      setOpenInvoiceDetailsId(null);
      setCartOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ── Fetch products ───────────────────────────────────────────────────────────
  // Network only when API filter params change (cached). Facility/sector/turnover/AB
  // recalculate client-side from the cached catalogue — same idea as BTL staying snappy.
  React.useEffect(() => {
    if (!inputsReady) {
      setResults([]);
      setCatalogueCount(0);
      setSourcingError("");
      setCalcSeconds("0.000");
      return;
    }

    let cancelled = false;
    const payload = buildInvoiceFinanceSourcingPayload({
      sector,
      facilityNum,
      turnoverNum,
      additionalBorrowingType,
      additionalBorrowingNum,
      filters,
    });
    const cacheHit = peekInvoiceFinanceCatalogueCache(payload);
    setLoading(true);
    setSourcingError("");
    const start = performance.now();

    const timer = window.setTimeout(() => {
      (async () => {
        try {
          const res = cacheHit || (await getInvoiceFinanceSourcingFilters(payload));
          if (cancelled) return;

          const rawProducts = res.products || [];
          const afterSector = rawProducts.filter(
            (p) =>
              !isExcludedInvoiceFinanceSector(p?.sector) &&
              matchesInvoiceFinanceSector(p?.sector, sector)
          );
          const catalogueProducts = filterInvoiceFinanceSourcingProducts(afterSector);

          if (process.env.NODE_ENV === "development") {
            console.log("📦 [IF Sourcing] filter pipeline:", {
              rawFromApi: rawProducts.length,
              afterSectorFilter: afterSector.length,
              afterDeactivatedFilter: catalogueProducts.length,
              selectedSector: sector,
              sampleSectors: rawProducts.slice(0, 5).map((p) => p?.sector),
            });
          }

          // Pass abValue + abType so each product calculates its own abCalc
          const abType = additionalBorrowingType === "percent" ? "%" : "£";
          const mapped = mapSourcingApiProducts(catalogueProducts, {
            facilityNum,
            turnoverNum,
            invoiceValue,
            abValue: additionalBorrowingValue,
            abType,
          }).map((row, index) => {
            const sourceProduct = catalogueProducts[index] || {};
            return calculateAdjustedProductCosts({ ...row, sourceProduct }, {
              facilityAmount: facilityNum,
              turnoverAmount: turnoverNum,
              utilisationPct: facilityUtilisationPct,
            });
          });

          // Soft eligibility (credit days / contract / concentration / export) then facility min/max.
          const eligible = mapped.filter((row) => productMatchesInvoiceEligibility(row, filters));
          const matched = eligible.filter((row) => productMatchesFacilityBand(row, facilityNum));

          if (process.env.NODE_ENV === "development") {
            console.log("📦 [IF Sourcing] eligibility pipeline:", {
              mapped: mapped.length,
              afterEligibility: eligible.length,
              afterFacilityBand: matched.length,
              facilityNum,
              turnoverNum,
              filters,
            });
            if (mapped.length > 0 && eligible.length === 0) {
              const sample = mapped[0];
              const raw = sample?._raw || sample?.sourceProduct || sample || {};
              console.log("📦 [IF Sourcing] sample product failing eligibility:", {
                maxConcen: normalizePercentScale(raw?.max_concen_percent),
                filterCreditDays: filters.creditTermsDays,
                termMonths: sample?.termMonths ?? sample?.contract_term_months ?? raw?.contract_term_months,
                filterContractTerm: filters.contractTermMonths,
                maxConcenRaw: raw?.max_concen_percent,
                filterConcentration: filters.concentrationLimit,
                maxLedgerDp: normalizePercentScale(raw?.max_ledger_dp),
                maxLedgerDpRaw: raw?.max_ledger_dp,
                filterExport: filters.exportLedger,
                ipPercent: normalizePercentScale(raw?.ip_percent ?? sample?.invoicePurchasePct),
                ipPercentRaw: raw?.ip_percent,
                homeOwner: sample?.homeOwner ?? raw?.home_owner,
                filterHomeOwner: filters.homeOwner,
              });
            }
            if (eligible.length > 0 && matched.length === 0) {
              const sample = eligible[0];
              const { min, max } = readProductLimitBand ? readProductLimitBand(sample) : {};
              console.log("📦 [IF Sourcing] sample product failing facility band:", {
                min, max, facilityNum,
              });
            }
          }

          setCatalogueCount(eligible.length);
          setResults(matched);
          setCalcSeconds(((performance.now() - start) / 1000).toFixed(3));
        } catch (err) {
          if (!cancelled) {
            setSourcingError(err?.message || "Failed to load sourcing products.");
            setResults([]);
            setCatalogueCount(0);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, cacheHit ? 0 : 300);

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [
    inputsReady, sector, facilityNum, turnoverNum, invoiceValue,
    facilityUtilisationPct,
    facilityRequired,
    additionalBorrowingType, additionalBorrowingNum, additionalBorrowingValue,
    filters.creditWorthiness, filters.creditTermsDays, filters.contractTermMonths,
    filters.contractualDebtAgreements, filters.proofOfDebt, filters.currentShortTermDebt,
    filters.selectiveInvoicing, filters.debtorProtection, filters.fundingLedgerType,
    filters.hmrcArrears, filters.previousLiquidatedCo, filters.directorExperience,
    filters.personalGuarantees, filters.confidentialFacility,
    filters.concentrationLimit, filters.exportLedger, filters.homeOwner,
  ]);

  // ── Sort ─────────────────────────────────────────────────────────────────────
  const sortedResults = React.useMemo(() => {
    const list = [...results];
    const cmpNum = (a, b, dir = "asc") => {
      const av = Number(a) || 0, bv = Number(b) || 0;
      return dir === "asc" ? av - bv : bv - av;
    };
    switch (sortBy) {
      case "cost":    list.sort((a, b) => cmpNum(a.costOverProductTerm, b.costOverProductTerm)); break;
      case "advance": list.sort((a, b) => cmpNum(a.netAdvance, b.netAdvance, "desc")); break;
      case "additional-loan": list.sort((a, b) => cmpNum(getProductAdditionalLoanValue(a, additionalBorrowingNum, additionalBorrowingType), getProductAdditionalLoanValue(b, additionalBorrowingNum, additionalBorrowingType), "desc")); break;
      case "mini-fee": list.sort((a, b) => cmpNum(getMinServiceFeeAmount(a, facilityNum), getMinServiceFeeAmount(b, facilityNum))); break;
      case "ip": list.sort((a, b) => cmpNum(a.invoicePurchasePct, b.invoicePurchasePct, "desc")); break;
      default: list.sort((a, b) => cmpNum(a.ratePct, b.ratePct)); break;
    }
    return list;
  }, [results, sortBy, facilityNum, additionalBorrowingNum, additionalBorrowingType]);

  const matchedProductId = React.useMemo(() => {
    if (!existingComparisonRow || !sortedResults.length) return null;
    return findBestMatchingProductId(existingComparisonRow, sortedResults);
  }, [existingComparisonRow, sortedResults]);

  // ── Misc effects ─────────────────────────────────────────────────────────────
  React.useEffect(() => { if (applicationMode !== "existing") setShowExistingFloat(true); }, [applicationMode]);

  React.useEffect(() => {
    if (!existingComparisonReady) setShowExistingFloat(true);
  }, [existingComparisonReady]);

  React.useEffect(() => {
    if (!cartOpen) { setCompareHighlight(false); return; }
    setCompareHighlight(true);
    const t = window.setTimeout(() => setCompareHighlight(false), 12200);
    return () => window.clearTimeout(t);
  }, [cartOpen]);

  cartMascotSuppressRef.current = !!(cartOpen || tourOpen || showOnboarding || showSignupPrompt);
  const cartMascotHideTimerRef = React.useRef(null);
  const cartMascotRetryTimerRef = React.useRef(null);
  const shortlistSizePrevRef = React.useRef(null);

  const hasSeenCartMascot = React.useCallback(() => {
    try {
      return sessionStorage.getItem(IF_CART_MASCOT_SEEN_KEY) === "1";
    } catch {
      return false;
    }
  }, []);

  const markCartMascotSeen = React.useCallback(() => {
    try {
      sessionStorage.setItem(IF_CART_MASCOT_SEEN_KEY, "1");
    } catch {
      /* ignore */
    }
  }, []);

  const revealCartMascot = React.useCallback(() => {
    if (hasSeenCartMascot()) return;
    if (cartMascotSuppressRef.current) {
      if (cartMascotRetryTimerRef.current) window.clearTimeout(cartMascotRetryTimerRef.current);
      cartMascotRetryTimerRef.current = window.setTimeout(() => {
        cartMascotRetryTimerRef.current = null;
        revealCartMascot();
      }, 1000);
      return;
    }
    setShowCartMascot(true);
    markCartMascotSeen();
    if (cartMascotHideTimerRef.current) window.clearTimeout(cartMascotHideTimerRef.current);
    cartMascotHideTimerRef.current = window.setTimeout(() => {
      cartMascotHideTimerRef.current = null;
      setShowCartMascot(false);
    }, 13000);
  }, [hasSeenCartMascot, markCartMascotSeen]);

  React.useEffect(() => {
    if (hasSeenCartMascot()) return undefined;
    const startTimer = window.setTimeout(() => {
      revealCartMascot();
    }, 15000);
    return () => {
      window.clearTimeout(startTimer);
      if (cartMascotHideTimerRef.current) window.clearTimeout(cartMascotHideTimerRef.current);
      if (cartMascotRetryTimerRef.current) window.clearTimeout(cartMascotRetryTimerRef.current);
    };
  }, [hasSeenCartMascot, revealCartMascot]);

  React.useEffect(() => {
    const prev = shortlistSizePrevRef.current;
    shortlistSizePrevRef.current = invoiceShortlist.size;
    if (prev === null) return;
    if (invoiceShortlist.size > prev) revealCartMascot();
  }, [invoiceShortlist.size, revealCartMascot]);

  React.useEffect(() => {
    if (cartOpen) setShowCartMascot(false);
  }, [cartOpen]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (cartOpen) { setCartOpen(false); return; }
      if (showExistingFloat && existingComparisonReady) {
        setShowExistingFloat(false);
        return;
      }
      setOpenInvoiceDetailsId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [cartOpen, showExistingFloat, existingComparisonReady]);

  // ── Auto-scroll only after the 4 "Your current facility" inputs match a product ─
  // Wait for the user to finish entering all 4, then bumpy-scroll past other cards.
  // Runs at most once per input+match key until Clear all / Reset.
  // If the user scrolls up (wheel / touch / keys) before or during the ride, cancel
  // and never auto-scroll for this match key.
  React.useEffect(() => {
    if (applicationMode !== "existing" || !existingComparisonReady || loading || !matchedProductId) return;
    if (!sortedResults.length) return;

    const runKey = [
      existingIp,
      existingDiscount,
      existingServiceFee,
      existingMiniServiceFee,
      matchedProductId,
    ].join("|");

    if (
      autoScrollKeyRef.current === runKey ||
      autoScrolledKeysRef.current.has(runKey)
    ) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let retryTimer = 0;
    let touchStartY = null;

    const lockAndAbort = () => {
      autoScrollKeyRef.current = runKey;
      autoScrolledKeysRef.current.add(runKey);
      cancelled = true;
      window.clearTimeout(retryTimer);
      if (typeof cancelBumpyScrollRef.current === "function") {
        cancelBumpyScrollRef.current();
        cancelBumpyScrollRef.current = null;
      }
    };

    const onUserScrollUp = () => {
      if (cancelled) return;
      if (
        autoScrollKeyRef.current === runKey ||
        autoScrolledKeysRef.current.has(runKey)
      ) {
        // Already locked — still abort an in-flight ride if user scrolls up mid-way.
        if (typeof cancelBumpyScrollRef.current === "function") {
          cancelBumpyScrollRef.current();
          cancelBumpyScrollRef.current = null;
        }
        return;
      }
      lockAndAbort();
    };

    const onWheel = (e) => {
      if (e.deltaY < 0) onUserScrollUp();
    };
    const onTouchStart = (e) => {
      touchStartY = e.touches?.[0]?.clientY ?? null;
    };
    const onTouchMove = (e) => {
      const y = e.touches?.[0]?.clientY;
      if (touchStartY == null || y == null) return;
      // Finger moves down → content scrolls up.
      if (y - touchStartY > 12) onUserScrollUp();
    };
    const onKeyDown = (e) => {
      if (e.key === "ArrowUp" || e.key === "PageUp" || e.key === "Home") {
        onUserScrollUp();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("keydown", onKeyDown);

    const tryScroll = () => {
      if (cancelled) return;
      // Locked while we waited — another pass already handled this key.
      if (
        autoScrollKeyRef.current === runKey ||
        autoScrolledKeysRef.current.has(runKey)
      ) {
        return;
      }

      const target = document.querySelector(
        `[data-invoice-product-id="${CSS.escape(String(matchedProductId))}"]`
      );
      const facilityPanel = document.querySelector('[data-role="existing-facility-float"]');
      if (!target || !facilityPanel) {
        if (attempts < 16) {
          attempts += 1;
          retryTimer = window.setTimeout(tryScroll, 80);
        }
        return;
      }

      // Lock as soon as we attempt, even if already in view — so returning to the
      // top of the page cannot trigger a second auto-scroll for the same inputs.
      autoScrollKeyRef.current = runKey;
      autoScrolledKeysRef.current.add(runKey);

      cancelBumpyScrollRef.current = bumpyScrollToProduct(target, {
        stepPx: 420,
        pauseMs: 800,
        settleMs: 80,
        margin: 24,
        topPad: 96,
        companions: [facilityPanel],
      }) || null;
    };

    // Brief pause so the user can finish the last field before the ride starts.
    const startTimer = window.setTimeout(tryScroll, 1000);

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      window.clearTimeout(retryTimer);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
      // Keep in-flight bumpy scroll + lock unless the user scrolled up (handled above).
      // Clearing the lock here would re-trigger after scrolling back to the top.
    };
  }, [
    applicationMode,
    existingComparisonReady,
    loading,
    sortedResults.length,
    matchedProductId,
    existingIp,
    existingDiscount,
    existingServiceFee,
    existingMiniServiceFee,
  ]);

  const calculatedAdditionalLoan = React.useMemo(() => {
    if (additionalBorrowingValue === "" || !Number.isFinite(additionalBorrowingNum) || additionalBorrowingNum <= 0) return null;
    if (!Number.isFinite(facilityNum) || facilityNum <= 0) return null;
    const extra = additionalBorrowingType === "percent" ? (facilityNum * additionalBorrowingNum) / 100 : additionalBorrowingNum;
    return Math.round(extra);
  }, [additionalBorrowingValue, additionalBorrowingNum, additionalBorrowingType, facilityNum]);

  const userRequestedAdditionalBorrowing =
    additionalBorrowingValue !== "" &&
    Number.isFinite(additionalBorrowingNum) &&
    additionalBorrowingNum > 0;

  const shortlistedArray = React.useMemo(() => {
    const out = [];
    if (invoiceShortlist.size > 0) {
      invoiceShortlist.forEach((id) => {
        const p = shortlistedInvoiceMap.get(id);
        if (p) out.push(p);
      });
      return out;
    }
    // Fallback if set was cleared but map still has hydrated items
    shortlistedInvoiceMap.forEach((p) => {
      if (p) out.push(p);
    });
    return out;
  }, [invoiceShortlist, shortlistedInvoiceMap]);

  const onApplyShortlist = React.useCallback(() => {
    if (shortlistedArray.length === 0) {
      setShortlistNotice("Add up to 5 products to proceed.");
      return;
    }

    if (isAuthenticated()) {
      navigate(shortlistPathForCategory("IF"), { replace: false });
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEYS.POST_AUTH_REDIRECT, shortlistPathForCategory("IF"));
    } catch {
      /* ignore */
    }

    navigate("/borrower/signup", {
      state: {
        shortlistedProducts: shortlistedArray.map((p) =>
          buildInvoiceFinanceShortlistProductData(p, {
            sector,
            projectedTurnover: turnoverNum,
            facilityRequired: facilityNum,
            additionalBorrowingNum: Number.isFinite(additionalBorrowingNum) ? additionalBorrowingNum : 0,
            additionalBorrowingType,
            filters,
          })
        ),
        sourcingInputs: {
          sector,
          projectedTurnover,
          facilityRequired,
          additionalBorrowingType,
          additionalBorrowingValue,
        },
        filters: { ...filters },
        productCategory: "IF",
      },
      replace: false,
    });
  }, [
    shortlistedArray,
    navigate,
    sector,
    turnoverNum,
    facilityNum,
    additionalBorrowingNum,
    additionalBorrowingType,
    additionalBorrowingValue,
    projectedTurnover,
    facilityRequired,
    filters,
  ]);

  const activeFilterCount = React.useMemo(
    () => countActiveInvoiceFilters(filters, facilityUtilisationPct, sector),
    [filters, facilityUtilisationPct, sector]
  );

  // ── Tour step gating ───────────────────────────────────────────────────────
  const tourFiltersChanged = React.useMemo(() => {
    const base = tourFiltersBaselineRef.current;
    if (!base) return false;
    return base.snapshot !== JSON.stringify({ filters, facilityUtilisationPct });
  }, [filters, facilityUtilisationPct]);

  const tourCanNext = React.useMemo(() => {
    switch (tourStepId) {
      case "invoice-quickbar": return inputsReady;
      case "invoice-results": return inputsReady && sortedResults.length > 0;
      case "invoice-filters": return tourFiltersChanged;
      case "invoice-add-to-shortlist": return invoiceShortlist.size > 0;
      case "invoice-shortlist-cart-window": return true;
      default: return true;
    }
  }, [tourStepId, inputsReady, sortedResults.length, tourFiltersChanged, invoiceShortlist.size]);

  const tourNextHint = React.useMemo(() => {
    if (tourCanNext) return "";
    switch (tourStepId) {
      case "invoice-quickbar": return "Pick a sector and enter projected turnover to load products.";
      case "invoice-results": return "Wait for matched facilities to appear in the results panel.";
      case "invoice-filters": return "Change at least one filter to narrow results.";
      case "invoice-add-to-shortlist": return "Add at least one facility to your shortlist (up to 5).";
      default: return "";
    }
  }, [tourCanNext, tourStepId]);

  const clearExistingFacilityInputs = React.useCallback(() => {
    setExistingIp("");
    setExistingDiscount("");
    setExistingServiceFee("");
    setExistingMiniServiceFee("");
    setShowExistingFloat(true);
    if (typeof cancelBumpyScrollRef.current === "function") {
      cancelBumpyScrollRef.current();
      cancelBumpyScrollRef.current = null;
    }
    autoScrollKeyRef.current = "";
    autoScrolledKeysRef.current.clear();
  }, []);

  const resetAllFilters = React.useCallback(() => {
    try {
      localStorage.removeItem(IF_SOURCING_QS_KEY);
      localStorage.removeItem(IF_SOURCING_FILTERS_KEY);
    } catch {
      /* ignore */
    }
    setApplicationMode("new");
    if (typeof cancelBumpyScrollRef.current === "function") {
      cancelBumpyScrollRef.current();
      cancelBumpyScrollRef.current = null;
    }
    autoScrollKeyRef.current = "";
    autoScrolledKeysRef.current.clear();
    setSector("");
    setProjectedTurnover("");
    setFacilityRequired("");
    setAdditionalBorrowingType("amount");
    setAdditionalBorrowingValue("");
    setFacilityUtilisationPct(60);
    setExistingIp("");
    setExistingDiscount("");
    setExistingServiceFee("");
    setExistingMiniServiceFee("");
    setShowExistingFloat(true);
    setFilters({ ...DEFAULT_INVOICE_FILTERS });
    facilityTouchedRef.current = false;
    autoFillKeyRef.current = "";
    restoreFacilityOnceRef.current = false;
  }, []);

  const filtersBody = (
    <InvoiceFiltersBody
      filters={filters}
      setF={setF}
      facilityUtilisationPct={facilityUtilisationPct}
      setFacilityUtilisationPct={setFacilityUtilisationPct}
      applicationMode={applicationMode}
      setApplicationMode={setApplicationMode}
      sector={sector}
      existingIp={existingIp}
      setExistingIp={setExistingIp}
      existingDiscount={existingDiscount}
      setExistingDiscount={setExistingDiscount}
      existingServiceFee={existingServiceFee}
      setExistingServiceFee={setExistingServiceFee}
      existingMiniServiceFee={existingMiniServiceFee}
      setExistingMiniServiceFee={setExistingMiniServiceFee}
      onClearExistingFacility={clearExistingFacilityInputs}
      normalizeNumberInput={normalizeNumberInput}
      existingComparisonReady={existingComparisonReady}
      showExistingFloat={showExistingFloat}
      setShowExistingFloat={setShowExistingFloat}
      onReset={resetAllFilters}
    />
  );

  const isGuest = !isAuthenticated();
  // Don't gate mid-tour, so the "add to shortlist" step keeps working on real cards.
  const gateActive = isGuest && !tourOpen && sortedResults.length > GUEST_FREE_PRODUCTS;
  const shownResults = gateActive ? sortedResults.slice(0, GUEST_FREE_PRODUCTS) : sortedResults;
  const hiddenResults = gateActive ? sortedResults.slice(GUEST_FREE_PRODUCTS) : [];

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .no-spinner::-webkit-outer-spin-button,.no-spinner::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
        .no-spinner{-moz-appearance:textfield;appearance:textfield}
        .hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none}
        .hide-scrollbar::-webkit-scrollbar{display:none;width:0;height:0}
        @keyframes borderShine{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes slicedWhiteFade{0%{background-position:-300% 0}50%{background-position:300% 0}100%{background-position:-300% 0}}
        @keyframes snore{0%{transform:translateY(0) scale(0.5);opacity:0}30%{opacity:1}100%{transform:translateY(-40px) translateX(20px) scale(1.5);opacity:0}}
        .snore-z{position:absolute;color:black;font-weight:bold;pointer-events:none;animation:snore 3s ease-in-out infinite;opacity:0}
        .cart-shiny-border{position:relative}
        .cart-shiny-border::before{content:'';position:absolute;inset:-3px;border-radius:1.25rem;background:linear-gradient(90deg,transparent 0%,transparent 25%,rgba(14,165,233,0.5) 30%,rgba(56,189,248,0.6) 35%,rgba(14,165,233,0.7) 40%,rgba(56,189,248,0.8) 45%,rgba(14,165,233,0.9) 47%,rgba(56,189,248,1) 50%,rgba(251,191,36,1) 50.5%,rgba(245,158,11,0.9) 51%,rgba(251,191,36,0.8) 52%,rgba(245,158,11,0.7) 55%,rgba(251,191,36,0.6) 60%,rgba(245,158,11,0.5) 65%,transparent 70%,transparent 100%);background-size:200% 100%;animation:borderShine 3s linear infinite;z-index:-1;padding:3px;-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);mask-composite:exclude}
        .cart-text-fade{position:relative;display:inline-block}
        .cart-text-fade::before{content:attr(data-text);position:absolute;top:0;left:0;width:100%;height:100%;background:linear-gradient(90deg,transparent 0%,transparent 30%,rgba(255,255,255,0.3) 35%,rgba(255,255,255,0.5) 40%,rgba(255,255,255,0.7) 45%,rgba(255,255,255,0.9) 50%,rgba(255,255,255,0.7) 55%,rgba(255,255,255,0.5) 60%,rgba(255,255,255,0.3) 65%,transparent 70%,transparent 100%);background-size:300% 100%;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;animation:slicedWhiteFade 30s linear infinite;pointer-events:none}
        @keyframes compareRingFade{to{opacity:0}}
        .compare-highlight-ring{position:relative;border-radius:0.375rem}
        .compare-highlight-ring::before{content:'';position:absolute;inset:-3px;border-radius:0.5rem;background:linear-gradient(90deg,transparent 0%,transparent 25%,rgba(14,165,233,0.5) 30%,rgba(56,189,248,1) 50%,rgba(251,191,36,1) 50.5%,rgba(245,158,11,0.5) 65%,transparent 70%,transparent 100%);background-size:200% 100%;animation:borderShine 5s linear 0s 2,compareRingFade 2s ease-in-out 10s forwards;z-index:0;padding:3px;-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);mask-composite:exclude;pointer-events:none}
        @keyframes shineSweep{0%{left:-100%}50%{left:100%}100%{left:100%}}
        .comingSoon-pill{background:linear-gradient(180deg,rgba(56,189,248,1) 0%,rgba(14,165,233,1) 100%);box-shadow:0 14px 30px rgba(56,189,248,.35);border:1px solid rgba(255,255,255,.4);position:relative;overflow:hidden}
        .comingSoon-pill::before{content:'';position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.7),transparent);animation:shineSweep 2.5s ease-in-out infinite}
        .comingSoon-dot{width:6px;height:6px;border-radius:999px;background:rgba(255,255,255,1);box-shadow:0 2px 4px rgba(0,0,0,.15);position:relative;z-index:1;flex-shrink:0}
      `}</style>

      <InvoiceOnboarding
        open={showOnboarding}
        onStartTour={() => {
          setShowOnboarding(false);
          tourFiltersBaselineRef.current = { snapshot: JSON.stringify({ filters, facilityUtilisationPct }) };
          setTourStepIndex(0);
          setTourOpen(true);
          try { localStorage.setItem("if_polar_onboarding_seen", "1"); } catch { /* ignore */ }
        }}
        onSkip={() => {
          setShowOnboarding(false);
          try { localStorage.setItem("if_polar_onboarding_seen", "1"); } catch { /* ignore */ }
        }}
      />

      <InvoiceTour
        open={tourOpen}
        stepIndex={tourStepIndex}
        onNext={() => {
          setTourStepIndex((prev) => {
            const next = prev + 1;
            if (next > INVOICE_TOUR_STEPS.length - 1) {
              setTourOpen(false);
              try { localStorage.setItem("if_polar_tour_done", "1"); } catch { /* ignore */ }
              return prev;
            }
            return next;
          });
        }}
        onPrev={() => setTourStepIndex((prev) => Math.max(prev - 1, 0))}
        onClose={() => {
          setTourOpen(false);
          try { localStorage.setItem("if_polar_tour_done", "1"); } catch { /* ignore */ }
        }}
        onEnsureStep={(id) => {
          if (id === "invoice-shortlist-cart-window") setCartOpen(true);
        }}
        canNext={tourCanNext}
        nextHint={tourNextHint}
      />

      <TopNav transparentOnTop={false} centered />

      {/* Hero */}
      <section className="relative w-full overflow-hidden hero-landscape-responsive sourcing-hero-responsive min-h-[280px] h-[min(52vh,24rem)] md:h-[480px] md:max-h-none md:min-h-[280px]">
        <img src="/invoice_finance.webp" alt="Invoice finance" className="sourcing-hero-img absolute inset-0 h-full w-full object-cover object-[58%_22%] md:object-center"
          onError={(e) => { e.currentTarget.alt = ""; e.currentTarget.style.opacity = "0.2"; }} />
        <div className="absolute inset-0 bg-slate-900/50 md:bg-slate-900/45" />
        <div className="relative z-10 h-full sourcing-hero-inner hero-inner">
          <div className="max-w-screen-xl mx-auto h-full px-4">
            <div className="flex h-full flex-col justify-end gap-3 md:grid md:grid-cols-2 md:items-start md:gap-8 md:justify-start min-h-0 pt-0 md:pt-28 pb-4 md:pb-0">
              <h1 className="text-white text-[1.65rem] leading-[1.15] sm:text-[30px] md:text-[40px] font-extrabold md:leading-[1.1]">
                <span className="md:hidden">Welcome to Your Personalised Sourcing Platform</span>
                <span className="hidden md:inline">
                  Welcome to Your <br /> Personalised Sourcing <br /> Platform
                </span>
              </h1>
              <p className="text-white/95 text-sm sm:text-[16px] md:text-[17px] leading-relaxed max-w-[560px]">
                Effortlessly compare and secure the best invoice finance facilities for your business.
                Register now to unlock powerful tools that guide you through every step—from tailored
                funding comparisons across leading UK lenders to real-time application tracking—so you
                stay informed and in control.
              </p>
              <div className="md:col-span-2 mt-1 md:mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setTourOpen(false);
                    setTourStepIndex(0);
                    setShowOnboarding(true);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600/90 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-sky-600 md:rounded-2xl md:gap-2 md:px-6 md:py-4 md:text-base md:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5 md:h-5 md:w-5 shrink-0" />
                  Tour guide
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick bar */}
      <div id="invoice-quickbar" className="mt-0 bg-sky-400 relative z-20 quickbar-landscape-responsive">
        <div className="max-w-screen-xl mx-auto px-4 pt-6 pb-8 sm:pt-8 sm:pb-10 relative z-30">
          {/* ── Inputs: Sector | Turnover | Facility required | Additional Borrowing ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">

            {/* Sector */}
            <div className="block">
              <div className="mb-1 text-white font-semibold text-[16px] md:text-[18px]">Sector</div>
              <SectorSelect
                value={sector}
                options={sectorOptions}
                className="!bg-white"
                onChange={(next) => {
                  setSector(next);
                  if (!isInvoiceFinanceConstructionSector(next)) {
                    setF("homeOwner", "");
                  }
                }}
              />
            </div>

            {/* Projected turnover — primary input */}
            <InputShell label="Projected turnover" labelClassName="text-white font-semibold text-[16px] md:text-[18px]">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-800 text-[16px] md:text-[18px]">£</span>
                <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0"
                  aria-invalid={projectedTurnoverError ? "true" : "false"}
                  className={"no-spinner w-full rounded-xl border ring-1 bg-white pl-8 pr-10 py-3 text-[16px] md:text-[18px] focus:outline-none focus:ring-1 " + (projectedTurnoverError ? "border-red-500 ring-red-300 focus:ring-red-400" : "border-sky-200 ring-slate-200 focus:ring-sky-300")}
                  value={projectedTurnover}
                  onChange={(e) => { const next = normalizeNumberInput(e.target.value, { allowDecimal: false }); if (next == null) return; setProjectedTurnover(next); }}
                  onBlur={() => showMobileQuickValidationIfNeeded("Projected turnover", projectedTurnoverError)}
                />
                <button type="button" aria-label="Clear projected turnover"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-sky-200 bg-sky-100 text-sky-700 hover:bg-sky-200 text-[18px] leading-none"
                  onClick={() => setProjectedTurnover("")}
                >×</button>
              </div>
              {projectedTurnoverError ? <p className="mt-1 text-xs font-bold text-white">{projectedTurnoverError}</p> : null}
            </InputShell>

            {/* Facility required — auto-filled from turnover, editable */}
            <InputShell label="Facility required" labelClassName="text-white font-semibold text-[16px] md:text-[18px]">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-800 text-[16px] md:text-[18px]">£</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0"
                  aria-invalid={facilityRequiredError ? "true" : "false"}
                  className={
                    "no-spinner w-full rounded-xl border ring-1 bg-white pl-8 pr-10 py-3 text-[16px] md:text-[18px] focus:outline-none focus:ring-1 " +
                    (facilityRequiredError
                      ? "border-red-500 ring-red-300 focus:ring-red-400"
                      : "border-sky-200 ring-slate-200 focus:ring-sky-300")
                  }
                  value={facilityRequired}
                  onChange={(e) => {
                    const next = normalizeNumberInput(e.target.value, { allowDecimal: false });
                    if (next == null) return;
                    facilityTouchedRef.current = true;
                    setFacilityRequired(next);
                  }}
                  onBlur={() => showMobileQuickValidationIfNeeded("Facility required", facilityRequiredError)}
                />
                <button
                  type="button"
                  aria-label="Clear facility required"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-sky-200 bg-sky-100 text-sky-700 hover:bg-sky-200 text-[18px] leading-none"
                  onClick={() => {
                    facilityTouchedRef.current = false;
                    if (autoFacilityNum > 0) {
                      setFacilityRequired(formatWithCommas(String(autoFacilityNum)));
                    } else {
                      setFacilityRequired("");
                    }
                  }}
                >
                  ×
                </button>
              </div>
              {facilityRequiredError ? (
                <p className="mt-1 text-xs font-bold text-white">{facilityRequiredError}</p>
              ) : null}
            </InputShell>

            {/* Additional borrowing — £ only */}
            <InputShell
              label={<>Additional Borrowing<span className="hidden md:inline"> (AB)</span></>}
              labelClassName="text-white font-semibold text-[16px] md:text-[18px] whitespace-nowrap truncate"
            >
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-800 text-[16px] md:text-[18px]">£</span>
                <input type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0"
                  aria-invalid={additionalBorrowingError ? "true" : "false"}
                  className={"no-spinner w-full rounded-xl border ring-1 bg-white pl-9 pr-10 py-3 text-[16px] md:text-[18px] focus:outline-none focus:ring-1 " + (additionalBorrowingError ? "border-red-500 ring-red-300 focus:ring-red-400" : "border-sky-200 ring-slate-200 focus:ring-sky-300")}
                  value={additionalBorrowingValue}
                  onChange={(e) => { const next = normalizeNumberInput(e.target.value, { allowDecimal: false }); if (next == null) return; setAdditionalBorrowingValue(next); }}
                  onBlur={() => showMobileQuickValidationIfNeeded("Additional borrowing", additionalBorrowingError)}
                />
                <button type="button" aria-label="Clear additional borrowing"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-sky-200 bg-sky-100 text-sky-700 hover:bg-sky-200 text-[18px] leading-none"
                  onClick={() => setAdditionalBorrowingValue("")}
                >×</button>
              </div>
              {additionalBorrowingError ? <p className="mt-1 text-xs font-bold text-white">{additionalBorrowingError}</p> : null}
            </InputShell>
            <MobileQuickValidationDialog
              fieldLabel={mobileValidationDialog?.fieldLabel}
              message={mobileValidationDialog?.message}
              onClose={() => setMobileValidationDialog(null)}
            />
          </div>
        </div>
      </div>

      {/* Mobile sticky Filters / Sort bar */}
      <div className="md:hidden sticky top-20 z-40 bg-gray-100 border-b border-slate-200">
        <div className="max-w-screen-xl mx-auto px-4 py-2.5">
          <div className="flex gap-2">
            <button
              type="button"
              id="invoice-mobile-filters-trigger"
              onClick={() => setMobileFiltersOpen(true)}
              className="relative flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <SlidersHorizontal className="h-4 w-4 text-slate-600" />
              Filters
              {activeFilterCount > 0 ? (
                <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-sky-500 px-1.5 text-[11px] font-bold text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => setMobileSortOpen(true)}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <SlidersHorizontal className="h-4 w-4 text-slate-600" />
              Sort
            </button>
            <button
              type="button"
              onClick={openReviewPrompt}
              className={SOURCING_FEEDBACK_MOBILE_BAR_BUTTON_CLASS}
            >
              <MessageSquareHeart className="h-4 w-4 shrink-0" />
              Feedback
            </button>
          </div>
        </div>
      </div>

      {/* Filters + Results */}
      <div className="max-w-screen-xl mx-auto px-4 -mt-2 md:-mt-3 relative z-30 min-h-0 pb-28 md:pb-24 overflow-visible">
        <div className="grid grid-cols-1 md:grid-cols-[320px,minmax(0,1fr)] gap-4 overflow-visible">

          {/* Filters sidebar — desktop only */}
          <div id="invoice-filters" className="hidden md:block rounded-2xl bg-gray-100 border border-slate-200 p-3.5 shrink-0 md:max-h-[85vh] md:overflow-y-auto">
            {filtersBody}
          </div>

          {/* Results panel */}
          <div id="invoice-results" className="scroll-mt-24 min-w-0 rounded-2xl bg-gray-100 border border-slate-200 p-3 pb-28 md:pb-3 min-h-[200px] overflow-x-visible overflow-y-visible">
            {shortlistNotice ? (
              <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {shortlistNotice}
              </div>
            ) : null}
            {!inputsReady ? (
              <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
                Select a sector and enter your projected turnover to load matched products. Facility is calculated automatically.
              </div>
            ) : sourcingError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">{sourcingError}</div>
            ) : loading && results.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-10 flex flex-col items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-sky-500 mb-4" />
                <p className="text-slate-600">Updating facilities…</p>
              </div>
            ) : results.length === 0 && !loading ? (
              <div className="rounded-xl border border-slate-200 bg-white">
                {catalogueCount > 0 ? (
                  <div className="p-6 text-sm text-slate-600">
                    Found{" "}
                    <span className="font-bold text-sky-800 bg-sky-100 px-1 rounded">
                      {catalogueCount} product{catalogueCount === 1 ? "" : "s"}
                    </span>{" "}
                    in this sector, but none match your{" "}
                    <span className="font-bold text-sky-800 bg-sky-100 px-1 rounded">facility</span>{" "}
                    of{" "}
                    <span className="font-bold text-sky-800 bg-sky-100 px-1 rounded">£{Num(facilityNum)}</span>
                    . Try a facility inside a product min/max limit window, or adjust projected turnover.
                  </div>
                ) : (
                  <SleepyPolarBearEmptyState />
                )}
              </div>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <SourcingLiveCount
                    count={sortedResults.length}
                    calcSeconds={calcSeconds}
                    loading={loading}
                    countSuffix={
                      facilityNum > 0
                        ? ` found in facility band £${Num(facilityNum)}`
                        : " found"
                    }
                  />
                  <div className="hidden md:block">
                    <InvoiceSortControl sortBy={sortBy} setSortBy={setSortBy} />
                  </div>
                </div>
                {shownResults.map((row, index) => {
                  const showExistingSide =
                    existingComparisonReady &&
                    showExistingFloat &&
                    existingComparisonRow &&
                    matchedProductId === row.id;
                  return (
                    <div key={row.id} className="relative mb-2.5">
                      <InvoiceResultCard
                        row={row}
                        tourAnchor={index === 0}
                        facilityAmount={facilityNum}
                        additionalLoanAmount={calculatedAdditionalLoan}
                        showAdditionalBorrowingChip={userRequestedAdditionalBorrowing && row.additionalBorrowing}
                        shortlisted={invoiceShortlist.has(row.id)}
                        shortlistIsFull={shortlistIsFull}
                        onToggleShortlist={() => toggleInvoiceShortlist(row.id, row)}
                        detailsOpen={openInvoiceDetailsId === row.id}
                        onToggleDetails={() => toggleInvoiceDetails(row.id)}
                        onTrackClick={trackClickForProduct}
                        isMatchHighlight={Boolean(
                          existingComparisonReady && matchedProductId && matchedProductId === row.id
                        )}
                      />
                      {showExistingSide ? (
                        <div
                          data-role="existing-facility-float"
                          className={
                            "z-[100] pointer-events-auto " +
                            "mt-4 w-full max-w-[min(280px,calc(100vw-2rem))] mx-auto " +
                            "lg:absolute lg:left-auto lg:right-full lg:top-0 lg:mt-0 lg:mx-0 lg:mr-4 " +
                            "lg:w-[min(260px,calc(100vw-2rem))] xl:mr-5"
                          }
                        >
                          <ExistingFacilityComparisonPanel
                            row={existingComparisonRow}
                            facilityAmount={facilityNum}
                            onClose={() => setShowExistingFloat(false)}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {gateActive && (
                  <InvoiceLockedResultsGate
                    hiddenRows={hiddenResults}
                    hiddenCount={hiddenResults.length}
                    totalCount={sortedResults.length}
                    facilityAmount={facilityNum}
                    onUnlock={() => {
                      setSignupPromptStartOnForm(true);
                      setShowSignupPrompt(true);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-black text-slate-300 pb-24 md:pb-0">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-xs text-slate-400 leading-relaxed">
            Dynesourcer Ltd is authorised and regulated by the Financial Conduct Authority under firm reference number 1050262. The information on this website is provided for general information only and does not constitute mortgage advice, investment advice, legal advice, tax advice, or a personal recommendation unless explicitly stated otherwise. You should carry out your own due diligence and obtain independent legal, tax, and financial advice before proceeding with any purchase or finance arrangement. Property investment and buy-to-let involve risk, and returns are not guaranteed.
          </p>
        </div>
      </footer>

      {/* Floating shortlist cart — desktop */}
      <CartMascotNudge open={showCartMascot} />
      <button ref={cartButtonRef} type="button" onClick={() => setCartOpen((v) => !v)}
        className={`!fixed !bottom-4 !right-3 md:!bottom-24 md:!right-6 z-[50] hidden md:inline-flex items-center justify-center rounded-full md:rounded-2xl bg-white p-2 md:px-6 md:py-4 shadow-md md:shadow-xl hover:shadow-lg md:hover:shadow-2xl transition-all border-2 border-sky-400 ${shortlistIsFull ? "ring-2 ring-amber-400" : ""} cart-shiny-border`}
        title="View shortlist" aria-label="View shortlist"
      >
        <span className="relative inline-flex items-center gap-1.5 md:gap-3 z-10">
          <ShoppingCart className="h-4 w-4 md:h-7 md:w-7 text-slate-700" />
          <span className="cart-text-fade hidden md:inline text-base font-semibold text-slate-800" data-text="Apply with Shortlist">Apply with Shortlist</span>
          <span className="inline-flex h-4 min-w-[1rem] md:h-7 md:min-w-[1.75rem] items-center justify-center rounded-full bg-sky-500 px-1 md:px-2.5 text-[10px] md:text-sm font-bold text-white">{invoiceShortlist.size}</span>
        </span>
      </button>

      {/* Mobile shortlist bar */}
      <div id="invoice-mobile-shortlist-bar" className="md:hidden fixed inset-x-0 bottom-0 z-[50] border-t border-slate-200 bg-white shadow-[0_-4px_20px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
        <button
          type="button"
          onClick={() => setCartOpen((v) => !v)}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-semibold text-slate-800 cart-shiny-border ${shortlistIsFull ? "ring-2 ring-inset ring-amber-300" : ""}`}
          aria-label="Apply with shortlist"
        >
          <ShoppingCart className="h-5 w-5 text-slate-700" />
          <span className="cart-text-fade" data-text="Apply with Shortlist">Apply with Shortlist</span>
          <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-sky-500 px-2 text-xs font-bold text-white">
            {invoiceShortlist.size}
          </span>
        </button>
      </div>

      {/* Shortlist cart popover */}
      {cartOpen && (
        <div ref={cartPopoverRef} id="invoice-shortlist-cart-window"
          className={`fixed bottom-[4.5rem] right-4 left-4 md:bottom-32 md:right-6 md:left-auto z-[50] rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden ${shortlistViewMode === "compare" ? "w-[min(95vw,1100px)]" : "w-[min(520px,95vw)]"}`}
          style={{ maxHeight: "80vh" }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="font-semibold text-slate-900">Shortlisted products (max 5)</div>
            <div className="flex items-center gap-3">
              {shortlistedArray.length > 0 && (
                <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-0.5">
                  <button className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${shortlistViewMode === "list" ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-slate-50"}`} onClick={() => setShortlistViewMode("list")}>List</button>
                  <span className={compareHighlight ? "compare-highlight-ring" : ""}>
                    <button className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${shortlistViewMode === "compare" ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-slate-50"}`} onClick={() => setShortlistViewMode("compare")}>Compare</button>
                  </span>
                </div>
              )}
              <button className="text-xs font-semibold text-slate-600 underline underline-offset-2 disabled:opacity-40" onClick={() => { setInvoiceShortlist(new Set()); setShortlistedInvoiceMap(new Map()); }} disabled={invoiceShortlist.size === 0}>Clear all</button>
              <button type="button" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100" onClick={() => setCartOpen(false)} aria-label="Close shortlist"><X className="h-5 w-5" /></button>
            </div>
          </div>

          <div className={`${shortlistViewMode === "compare" ? "max-h-[60vh]" : "max-h-[50vh]"} overflow-auto min-h-0 ${shortlistViewMode === "list" ? "divide-y divide-slate-100" : ""}`}>
            {shortlistedArray.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No products shortlisted yet.</div>
            ) : shortlistViewMode === "compare" ? (
              <div className="p-2 md:p-4">
                <div className="overflow-x-auto -mx-2 px-2 md:-mx-4 md:px-4">
                  <table className="w-full border-collapse min-w-[320px]">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-2 px-2 md:py-3 md:px-3 text-[11px] md:text-xs font-semibold text-slate-600 uppercase tracking-wide sticky left-0 bg-white z-10 min-w-[110px]">Feature</th>
                        {shortlistedArray.map((p) => (
                          <th key={p.id} className="text-center py-2 px-2 text-[11px] md:text-xs font-semibold text-slate-900 min-w-[140px] border-l border-slate-200">
                            <div className="flex flex-col items-center gap-1.5">
                              <div className="text-[11px] md:text-xs font-semibold text-slate-900 leading-snug">
                                {p.displayTitle || formatInvoiceFinanceProductTitle(p)}
                              </div>
                              <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden hide-scrollbar touch-pan-x overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                                <div className="flex w-max max-w-none justify-center">
                                  <InvoiceProductChips
                                    invoicePurchasePct={p.invoicePurchasePct}
                                    dpAllowed={p.dpAllowed}
                                    additionalLoanAmount={calculatedAdditionalLoan}
                                    maxAbAmount={p.maxAbAmount}
                                    showAdditionalBorrowingChip={userRequestedAdditionalBorrowing && p.additionalBorrowing}
                                    reviewLimitAmount={calcReviewLimit(facilityNum)}
                                    compact
                                    nowrap
                                  />
                                </div>
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: "rate", label: "APR", render: (p) => `${Number(p.ratePct).toFixed(2)}%` },
                        { key: "ip", label: "IP", render: (p) => `${Math.round(Number(p.invoicePurchasePct) || 0)}%` },
                        { key: "advance", label: "Net advance", render: (p) => `£${Num(p.netAdvance)}` },
                        { key: "mini", label: "Min. service fee", render: (p) => `£${Num(getMinServiceFeeAmount(p, facilityNum))}` },
                        { key: "term", label: "Contract term", render: (p) => {
                          const months = resolveInvoiceFinanceContractTermMonths(p);
                          return months != null ? `${months} months` : "—";
                        } },
                        { key: "cost", label: "Cost over product term", render: (p) => `£${Num(p.costOverProductTerm)}` },
                      ].map((r) => (
                        <tr key={r.key} className={"border-b border-slate-100 " + (r.key === "advance" || r.key === "cost" ? "bg-sky-50" : "hover:bg-slate-50")}>
                          <td className={"py-2 px-2 md:py-3 md:px-3 text-xs md:text-sm font-medium text-slate-700 sticky left-0 z-10 " + (r.key === "advance" || r.key === "cost" ? "bg-sky-50" : "bg-white")}>{r.label}</td>
                          {shortlistedArray.map((p) => (
                            <td key={`${r.key}-${p.id}`} className={"py-2 px-2 text-center text-xs md:text-sm text-slate-900 border-l border-slate-200 " + (r.key === "advance" || r.key === "cost" ? "bg-sky-50 font-bold" : "")}>
                              <span className="font-semibold">{r.render(p)}</span>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              shortlistedArray.map((p) => (
                <div key={p.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {p.displayTitle || formatInvoiceFinanceProductTitle(p)}
                      </div>
                      <div className="mt-2 text-sm text-slate-700 space-y-1">
                        {[["APR", `${Number(p.ratePct).toFixed(2)}%`], ["Net advance", `£${Num(p.netAdvance)}`], ["Total cost", `£${Num(p.costOverProductTerm)}`]].map(([label, val]) => (
                          <div key={label} className="flex items-center justify-between gap-4">
                            <span className="text-slate-500">{label}</span>
                            <span className="font-semibold">{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button type="button" className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100" onClick={() => toggleInvoiceShortlist(p.id, p)} title="Remove from shortlist">
                      <Trash2 className="h-4 w-4" />Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-slate-200 p-3">
            <button
              id="invoice-continue-shortlist"
              type="button"
              onClick={onApplyShortlist}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              disabled={shortlistedArray.length === 0}
            >
              Continue with shortlisted products
            </button>
          </div>
        </div>
      )}

      <MobileFiltersDrawer
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        productCount={sortedResults.length}
        onShow={() => setMobileFiltersOpen(false)}
        onClearAll={resetAllFilters}
        filtersBody={filtersBody}
      />

      <MobileSortDrawer
        open={mobileSortOpen}
        onClose={() => setMobileSortOpen(false)}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      <button
        type="button"
        onClick={openReviewPrompt}
        className={SOURCING_FEEDBACK_BUTTON_CLASS}
      >
        <MessageSquareHeart className="h-4 w-4" />
        Feedback
      </button>

      <SourcingSignupPrompt
        open={showSignupPrompt && !showOnboarding && !tourOpen}
        onClose={dismissSignupPrompt}
        onSignupSuccess={handleSignupPromptSuccess}
        startOnForm={signupPromptStartOnForm}
        category="IF"
        title="Keep comparing invoice finance products"
      />

      <ReviewPromptModal
        open={reviewPromptOpen}
        onClose={dismissReviewPrompt}
        productCategory="IF"
        contextLabel="How was Invoice Finance sourcing today?"
        defaultType="General Feedback"
      />
    </>
  );
}
